<template>
  <div class="pdp-analysis-wrap">
    <div
      v-if="reportData && isIframe"
      class="header-block-iframe mb-3"
      style="position: sticky; top: 0; z-index: 100;"
    >
      <div
        class="px-4 py-3"
        style="background-color: #eff8f5;"
      >
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <div style="color: #209c86; font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.25rem;">
              PDP Analysis
            </div>
            <h1 style="color: #02163b; font-size: 1.25rem; font-weight: 700; margin: 0 0 0.25rem 0; line-height: 1.3;">
              {{ reportData?.product_name }}
            </h1>
            <div style="color: #6b7280; font-size: 0.813rem;">
              <span>ID: {{ getId(reportData.url) }}</span>
            </div>
          </div>
          <div>
            <a
              href="https://itsrapid.ai/image-optimization/"
              target="_blank"
              style="text-decoration: none; font-size: 1.125rem; font-weight: 600; white-space: nowrap;"
            >
              <span style="color: #209c86;">It's</span><span style="color: #000;">Rapid</span> <span style="color: #209c86;">Optix</span><span style="color: #6b7280;">™</span>
            </a>
          </div>
        </div>
      </div>
    </div>
    <button
      v-if="isIframe"
      type="button"
      class="back-to-reports-btn mb-3"
      onclick="(function () {
                  if (window.parent) {
                      window.parent.dispatchEvent(
                          new CustomEvent('optix:back-to-dashboard', {
                              detail: {
                                  source: 'report-iframe'
                              }
                          })
                      );
                  }
              })()"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M19 12H5M5 12l7-7M5 12l7 7" />
      </svg>
      <span>Back to Dashboard</span>
    </button>
    <div class="d-flex justify-content-between align-items-center">
      <div>
        <h5 class="font-weight-bold mb-1">
          Product Detail Page Analysis
        </h5>
        <p class="text-muted mb-0">
          Analyze and optimize your product descriptions with AI-powered insights and keyword tracking
        </p>
      </div>
    </div>
    <section
      v-if="reports.length"
      class="urls-section source-sections mt-3"
    >
      <div class="space-y-2">
        <div class="d-flex align-items-center gap-2">
          <!-- Thumb + lightbox trigger -->
          <button
            v-if="selectedReport?.images?.length"
            type="button"
            class="p-0 border-0 bg-transparent img-thumb"
            aria-label="Open image"
            @click="openLightbox(selectedReport.images[0]?.src)"
          >
            <img
              :src="selectedReport.images[0]?.src + '&lightweight=1&scale_width=120'"
              alt=""
              width="40"
              height="40"
              class="img-thumb__img"
              loading="lazy"
            >
          </button>

          <div
            v-click-outside="closeReportDropdown"
            class="report-multiselect"
          >
            <button
              type="button"
              class="report-dropdown-trigger"
              @click="toggleReportDropdown"
            >
              <span
                v-if="selectedReport"
                class="report-dropdown-trigger__text"
              >
                <span class="report-dropdown-trigger__id">{{ selectedReport.productId }}</span>
                <span class="report-dropdown-trigger__sep">&mdash;</span>
                <span class="report-dropdown-trigger__name">{{ selectedReport.productName }}</span>
                <span
                  v-if="selectedReport"
                  class="report-score-badge bg-success"
                  :title="`${scoreValue}`"
                >
                  {{ scoreValue }}
                </span>
              </span>
              <span
                v-else
                class="report-dropdown-trigger__placeholder"
              >Choose product...</span>
              <i
                class="bi"
                :class="isReportDropdownOpen ? 'bi-chevron-up' : 'bi-chevron-down'"
              />
            </button>

            <div
              v-if="isReportDropdownOpen"
              class="report-dropdown-menu"
            >
              <div class="report-dropdown-search">
                <i class="bi bi-search report-dropdown-search__icon" />
                <input
                  ref="reportSearchInput"
                  v-model="reportSearchQuery"
                  type="text"
                  class="report-dropdown-search__input"
                  placeholder="Search by ASIN or name..."
                >
              </div>
              <ul class="report-dropdown-list">
                <li
                  v-for="option in filteredReportList"
                  :key="option.id"
                  class="report-dropdown-item"
                  :class="{ 'is-selected': selectedReport && selectedReport.id === option.id }"
                  @click="selectReport(option)"
                >
                  <i
                    v-if="selectedReport && selectedReport.id === option.id"
                    class="bi bi-check2 report-dropdown-item__check"
                  />
                  <span class="report-dropdown-item__id">{{ option.productId }}</span>
                  <span class="report-dropdown-item__name">{{ option.productName }}</span>
                  <span
                    class="report-dropdown-item__score bg-success"
                    :title="`${option.score ?? 0}`"
                  >
                    {{ option.score ?? 0 }}
                  </span>
                </li>
                <li
                  v-if="!filteredReportList.length"
                  class="report-dropdown-item report-dropdown-item--empty"
                >
                  No products found
                </li>
              </ul>
            </div>
          </div>

          <b-dropdown
            v-if="!isShared"
            variant="light"
            right
            no-caret
            class="d-inline-block"
          >
            <template #button-content>
              <i
                :class="{
                  'bi bi-sort-alpha-down': sortMode === 'alpha',
                  'bi bi-sort-down': sortMode === 'score_desc',
                  'bi bi-sort-up': sortMode === 'score_asc'
                }"
              />
            </template>

            <b-dropdown-item @click="setSortMode('alpha')">
              <i class="bi bi-sort-alpha-down me-2" />
              Alphabetical (A → Z)
            </b-dropdown-item>

            <b-dropdown-item @click="setSortMode('score_desc')">
              <i class="bi bi-sort-down me-2" />
              Score (Best → Worst)
            </b-dropdown-item>

            <b-dropdown-item @click="setSortMode('score_asc')">
              <i class="bi bi-sort-up me-2" />
              Score (Worst → Best)
            </b-dropdown-item>
          </b-dropdown>

          <div
            v-if="!isShared"
            v-click-outside="closeReportHistory"
            class="report-history-wrap"
          >
            <button
              class="btn btn-light report-history-btn"
              aria-label="History"
              @click="toggleReportHistory"
            >
              <i class="bi bi-clock-history" />
              History
              <span
                v-if="historyDateFilter !== 'any' || historyUserFilter"
                class="report-history-btn__dot"
              />
              <span class="report-history-btn__count">{{ filteredHistoryList.length }}</span>
            </button>

            <div
              v-if="isReportHistoryOpen"
              class="report-history-panel"
              @click.stop="closeHistoryDropdowns"
            >
              <div class="report-history-panel__header">
                <h3 class="report-history-panel__title">
                  Report History
                </h3>
                <div class="report-history-panel__scope-toggle">
                  <button
                    type="button"
                    class="report-history-panel__scope-btn"
                    :class="{ 'is-active': historyScope === 'product' }"
                    @click="setHistoryScope('product')"
                  >
                    This product
                  </button>
                  <button
                    type="button"
                    class="report-history-panel__scope-btn"
                    :class="{ 'is-active': historyScope === 'all' }"
                    @click="setHistoryScope('all')"
                  >
                    All
                  </button>
                </div>
              </div>

              <div class="report-history-panel__filters">
                <!-- Date filter -->
                <div class="report-history-filter">
                  <i class="bi bi-calendar3 report-history-filter__icon" />
                  <div
                    v-click-outside="closeDateDropdown"
                    class="report-history-filter__select-wrap"
                    @click.stop
                  >
                    <button
                      type="button"
                      class="report-history-filter__select"
                      @click="isDateFilterOpen = !isDateFilterOpen"
                    >
                      <span>{{ dateFilterLabel }}</span>
                      <i class="bi bi-chevron-down" />
                    </button>
                    <ul
                      v-if="isDateFilterOpen"
                      class="report-history-filter__dropdown"
                    >
                      <li
                        v-for="opt in dateFilterOptions"
                        :key="opt.value"
                        class="report-history-filter__dropdown-item"
                        :class="{ 'is-selected': historyDateFilter === opt.value }"
                        @click="setDateFilter(opt.value)"
                      >
                        <i
                          v-if="historyDateFilter === opt.value"
                          class="bi bi-check2"
                        />
                        {{ opt.label }}
                      </li>
                    </ul>
                  </div>
                  <div
                    v-if="historyDateFilter === 'specific'"
                    v-click-outside="closeDatePicker"
                    class="report-history-filter__pick-wrap"
                    @click.stop
                  >
                    <button
                      type="button"
                      class="btn btn-light btn-sm report-history-filter__pick-btn"
                      @click="isDatePickerOpen = !isDatePickerOpen"
                    >
                      <i class="bi bi-calendar3" />
                      {{ historySpecificDate ? formatHistoryDate(historySpecificDate) : 'Pick' }}
                    </button>
                    <div
                      v-if="isDatePickerOpen"
                      class="report-history-datepicker"
                    >
                      <div class="report-history-datepicker__header">
                        <button
                          type="button"
                          class="report-history-datepicker__nav"
                          @click="datePickerPrevMonth"
                        >
                          <i class="bi bi-chevron-left" />
                        </button>
                        <span class="report-history-datepicker__month-label">
                          {{ datePickerMonthLabel }}
                        </span>
                        <button
                          type="button"
                          class="report-history-datepicker__nav"
                          @click="datePickerNextMonth"
                        >
                          <i class="bi bi-chevron-right" />
                        </button>
                      </div>
                      <div class="report-history-datepicker__weekdays">
                        <span
                          v-for="d in ['Su','Mo','Tu','We','Th','Fr','Sa']"
                          :key="d"
                        >{{ d }}</span>
                      </div>
                      <div class="report-history-datepicker__days">
                        <button
                          v-for="(day, idx) in datePickerDays"
                          :key="idx"
                          type="button"
                          class="report-history-datepicker__day"
                          :class="{
                            'is-other-month': !day.currentMonth,
                            'is-selected': day.isSelected,
                            'is-today': day.isToday,
                            'is-disabled': !day.hasReport
                          }"
                          :disabled="!day.hasReport"
                          @click="day.hasReport && pickDate(day)"
                        >
                          {{ day.day }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- User filter -->
                <div class="report-history-filter">
                  <i class="bi bi-person report-history-filter__icon" />
                  <div
                    v-click-outside="closeUserDropdown"
                    class="report-history-filter__select-wrap"
                    @click.stop
                  >
                    <button
                      type="button"
                      class="report-history-filter__select"
                      @click="isUserFilterOpen = !isUserFilterOpen"
                    >
                      <span>{{ historyUserFilter ? historyUserFilter : 'All users' }}</span>
                      <i class="bi bi-chevron-down" />
                    </button>
                    <ul
                      v-if="isUserFilterOpen"
                      class="report-history-filter__dropdown"
                    >
                      <li
                        class="report-history-filter__dropdown-item"
                        :class="{ 'is-selected': !historyUserFilter }"
                        @click="setUserFilter(null)"
                      >
                        <i
                          v-if="!historyUserFilter"
                          class="bi bi-check2"
                        />
                        All users
                      </li>
                      <li
                        v-for="userName in historyUserNames"
                        :key="userName"
                        class="report-history-filter__dropdown-item"
                        :class="{ 'is-selected': historyUserFilter === userName }"
                        @click="setUserFilter(userName)"
                      >
                        <i
                          v-if="historyUserFilter === userName"
                          class="bi bi-check2"
                        />
                        {{ userName }}
                      </li>
                    </ul>
                  </div>
                  <button
                    v-if="historyDateFilter !== 'any' || historyUserFilter"
                    type="button"
                    class="btn btn-link btn-sm text-muted report-history-filter__clear-btn"
                    @click="clearHistoryFilters"
                  >
                    <i class="bi bi-x" />
                    Clear
                  </button>
                </div>
              </div>

              <!-- History list -->
              <div class="report-history-list">
                <div
                  v-for="item in filteredHistoryList"
                  :key="item.id + '-' + item.created_at"
                  class="report-history-list__item"
                  :class="{ 'is-active': selectedReport && selectedReport.id === item.id }"
                  @click="selectHistoryReport(item)"
                >
                  <i class="bi bi-file-earmark-text report-history-list__icon" />
                  <div class="report-history-list__info">
                    <div class="report-history-list__name">
                      {{ getId(item.url) }} &middot; {{ item.product_name }}
                    </div>
                    <div class="report-history-list__meta">
                      {{ formatHistoryDate(item.created_at) }}
                      <span v-if="getReportUserName(item)">
                        &middot; {{ getReportUserName(item) }}
                      </span>
                    </div>
                  </div>
                  <span
                    v-if="item.product_name !== 'Processing...'"
                    class="report-history-list__score bg-success"
                  >
                    {{ item.analysis_content?.score ?? 0 }}
                  </span>
                </div>
                <div
                  v-if="!filteredHistoryList.length"
                  class="report-history-list__empty"
                >
                  No reports found
                </div>
              </div>
            </div>
          </div>

          <button
            v-if="!isShared"
            class="btn btn-light"
            aria-label="Add products"
            @click="toggleAddProducts"
          >
            <i
              class="bi"
              :class="isAddProductsExpanded ? 'bi-dash' : 'bi-plus'"
            />
          </button>

          <b-dropdown
            v-if="selectedReport && !isShared"
            variant="light"
            right
            no-caret
          >
            <template #button-content>
              <i class="bi bi-three-dots" />
            </template>

            <b-dropdown-item @click="viewProduct(selectedReport)">
              <i class="bi bi-arrow-up-right-square me-2" /> View Product
            </b-dropdown-item>

            <b-dropdown-item @click="copyAction('id')">
              <i class="bi bi-copy me-2" /> Copy Product ID
            </b-dropdown-item>

            <b-dropdown-item @click="copyAction('url')">
              <i class="bi bi-link-45deg me-2" /> Copy URL
            </b-dropdown-item>

            <b-dropdown-item
              :disabled="updatingReports.includes(selectedReport.id)"
              @click="regenerateReport"
            >
              <i class="bi bi-arrow-clockwise me-2" /> Re-generate Report
            </b-dropdown-item>

            <b-dropdown-item
              :disabled="updatingReports.includes(selectedReport.id)"
              @click="regenerateReportImages"
            >
              <i class="bi bi-arrow-clockwise me-2" /> Re-generate Images
            </b-dropdown-item>

            <b-dropdown-item
              v-if="!isShared"
              @click="shareReport"
            >
              <i class="bi bi-share me-2" /> Share Report
            </b-dropdown-item>

            <b-dropdown-divider />

            <b-dropdown-item
              :disabled="!selectedReport.images?.length"
              @click="publishReport"
            >
              <i class="bi bi-cloud-upload" /> Publish...
            </b-dropdown-item>

            <b-dropdown-divider />

            <b-dropdown-item
              @click="xlsxExport(selectedReport.id)"
            >
              <i class="bi bi-download" /> Export Selected Item
            </b-dropdown-item>

            <b-dropdown-item
              @click="xlsxExport()"
            >
              <i class="bi bi-file-earmark-excel" /> Export All Items
            </b-dropdown-item>

            <b-dropdown-divider />

            <b-dropdown-item
              @click="xlsxOptimizationAfterExport(selectedReport.id)"
            >
              <i class="bi bi-download" /> Export Optimized Item
            </b-dropdown-item>

            <b-dropdown-item
              @click="xlsxOptimizationAfterExport()"
            >
              <i class="bi bi-file-earmark-excel" /> Export All Optimized Items
            </b-dropdown-item>

            <b-dropdown-divider />

            <b-dropdown-item
              class="text-danger"
              :disabled="updatingReports.includes(selectedReport.id)"
              @click="deleteReport"
            >
              <i class="bi bi-trash me-2" /> Remove Product
            </b-dropdown-item>
          </b-dropdown>

          <b-dropdown
            variant="light"
            right
            no-caret
            toggle-class="reports-type-dropdown__toggle"
            menu-class="reports-type-dropdown__menu"
          >
            <template #button-content>
              <i class="bi bi-gear" />
            </template>

            <div class="reports-type-dropdown__header">
              <div class="reports-type-dropdown__title">
                Prompt settings
              </div>
              <div class="reports-type-dropdown__subtitle">
                Choose which prompt renders each report tab.
              </div>
            </div>

            <div class="reports-type-dropdown__tabs">
              <button
                type="button"
                class="reports-type-dropdown__tab"
                :class="{ 'is-active': promptSettingsTab === 'title_copy' }"
                @click="setPromptSettingsTab('title_copy')"
              >
                Title + Copy
              </button>
              <button
                type="button"
                class="reports-type-dropdown__tab"
                :class="{ 'is-active': promptSettingsTab === 'images' }"
                @click="setPromptSettingsTab('images')"
              >
                Images
              </button>
            </div>

            <div class="reports-type-dropdown__current">
              <div class="reports-type-dropdown__current-label">
                Current {{ promptSettingsTab === 'images' ? 'Images' : 'Title + Copy' }} prompt
              </div>
              <div class="reports-type-dropdown__current-value">
                {{ activePromptSettingsOption ? activePromptSettingsOption.label : 'Default Prompt' }}
              </div>
            </div>

            <div class="reports-type-dropdown__search">
              <i class="bi bi-search reports-type-dropdown__search-icon" />

              <input
                v-model.trim="promptSearchQuery"
                type="text"
                class="reports-type-dropdown__search-input"
                placeholder="Search prompts..."
                @click.stop
                @keydown.stop
              >

              <button
                v-if="promptSearchQuery"
                type="button"
                class="reports-type-dropdown__search-clear"
                aria-label="Clear prompt search"
                @click.stop="promptSearchQuery = ''"
              >
                <i class="bi bi-x" />
              </button>
            </div>

            <div
              v-if="filteredPromptOptions"
              class="reports-type-dropdown__list"
            >
              <button
                v-for="option in filteredPromptOptions"
                :key="option.value"
                type="button"
                class="reports-type-dropdown__option"
                :class="{ 'is-active': activePromptSettingsValue === option.value }"
                @click="selectPromptForActiveTab(option.value)"
              >
                <span class="reports-type-dropdown__radio">
                  <span
                    v-if="activePromptSettingsValue === option.value"
                    class="reports-type-dropdown__radio-inner"
                  />
                </span>

                <span class="reports-type-dropdown__content">
                  <span class="reports-type-dropdown__label">
                    {{ option.label }}
                  </span>
                  <span class="reports-type-dropdown__description">
                    {{ option.description }}
                  </span>
                </span>
              </button>
            </div>
          </b-dropdown>

          <!-- Lightbox -->
          <div
            v-if="lightbox.isOpen"
            class="lightbox"
            tabindex="-1"
            @click.self="closeLightbox"
            @keydown.esc="closeLightbox"
          >
            <button
              type="button"
              class="lightbox__close"
              aria-label="Close"
              @click="closeLightbox"
            >
              <i class="bi bi-x-lg" />
            </button>

            <img
              :src="lightbox.src"
              alt=""
              class="lightbox__img"
            >
          </div>
        </div>
      </div>
    </section>

    <div
      v-if="lastAnalyzedAt"
      class="last-analyzed"
    >
      <i class="bi bi-clock-history" />
      Last analyzed {{ formatRelativeTime(lastAnalyzedAt) }}
    </div>

    <div class="mt-3 mb-4">
      <div
        class="prompts-list"
        :class="{'items-5': pdpTabs.length === 5}"
      >
        <div
          v-for="prompt in pdpTabs"
          :key="prompt.id"
          class="prompt-item"
          :class="{active: selectedPromptId === prompt.id, 'iframe-tab': isIframe}"
          @click="selectPrompt(prompt)"
        >
          <div class="prompt-item__title">
            <div class="icon-wrap">
              <i
                v-if="prompt.icon_type === 'string'"
                :class="prompt.icon"
              />
              <component
                :is="prompt.icon"
                v-else
                width="14"
              />
            </div>
            <p class="prompt-item__title__text">
              {{ prompt.title }}
            </p>
          </div>
        </div>
      </div>
    </div>
    
    <div
      v-if="isAddProductsExpanded && selectedPromptId !== 4"
      class="d-flex justify-content-between align-items-center"
    >
      <div>
        <h5
          class="text-lg font-semibold text-foreground mb-0"
        >
          Add Products
        </h5>
        <p
          class="text-muted font-sm mb-0"
        >
          <span>
            Choose how you want to add products to analyze
          </span>
        </p>
      </div>
    </div>
    
    <section
      v-if="isAddProductsExpanded && selectedPromptId !== 4"
      class="source-grid mt-3 mb-3"
    >
      <a
        v-for="source in sources"
        :key="source.id"
        :class="['source-card', { 'source-card--active': selectedSource === source.id }]"
        href="#"
        @click="selectSource($event, source)"
      >
        <span
          class="source-card__icon"
          aria-hidden="true"
        >
          <i :class="source.icon" />
        </span>
        <div class="source-card__body">
          <h3 class="source-card__title">{{ source.title }}</h3>
          <p class="source-card__desc">{{ source.description }}</p>
        </div>
      </a>
    </section>
    
    <section
      v-if="selectedSource === 'asin_urls' && !selectedReport && selectedPromptId !== 4"
      class="urls-section source-sections mt-3"
    >
      <div class="space-y-2">
        <div
          class="product-list-section mb-4"
          style="border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px;"
        >
          <div
            class="accordion-header d-flex justify-content-between align-items-center"
            style="user-select: none;"
          >
            <span
              style="cursor: pointer; flex: 1;"
              class="text-sm font-medium"
              @click="isProductListExpanded = !isProductListExpanded"
            >
              Select from Product Lists
            </span>
            <div
              v-if="isProductListExpanded && availableProducts.length > 0"
              class="d-flex align-items-center gap-2 mr-3"
              @click.stop
            >
              <a
                href="#"
                class="text-sm"
                style="text-decoration: none;"
                :class="{ 'text-muted': isLoadingProducts || filteredProducts.length === 0 }"
                @click.prevent="(!isLoadingProducts && filteredProducts.length > 0) && selectAllProducts()"
              >
                <i :class="selectedProducts.length === filteredProducts.length && filteredProducts.length > 0 ? 'bi bi-check-square' : 'bi bi-square'" />
                Select All
              </a>
              <select
                v-model="productTypeFilter"
                class="form-select form-select-sm product-select-all"
                @change="handleTypeFilterChange"
              >
                <option value="all">
                  All
                </option>
                <option
                  v-for="type in availableProductTypes"
                  :key="type"
                  :value="type"
                >
                  {{ type }}
                </option>
              </select>
            </div>
            <i
              style="cursor: pointer;"
              :class="isProductListExpanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"
              @click="isProductListExpanded = !isProductListExpanded"
            />
          </div>
          <div
            v-show="isProductListExpanded"
            class="row mt-2"
          >
            <div class="col-md-6 pr-1">
              <Multiselect
                v-model="selectedProductLists"
                :options="productListOptions"
                :multiple="true"
                :searchable="true"
                :loading="isLoadingProductLists"
                :disabled="isLoadingProductLists"
                track-by="id"
                label="name"
                placeholder="Choose product lists..."
                @input="loadProductsFromLists"
              />
            </div>
            <div
              v-if="availableProducts.length > 0"
              class="col-md-6 pl-1"
            >
              <Multiselect
                v-model="selectedProducts"
                :options="filteredProducts"
                :multiple="true"
                :searchable="true"
                :disabled="isLoadingProducts"
                :loading="isLoadingProducts"
                :close-on-select="false"
                track-by="id"
                label="display_name"
                placeholder="Select specific products"
                :allow-empty="true"
                :limit="3"
                :limit-text="limitText"
                :max-height="300"
                @input="populateSelectedProducts"
              >
                <template
                  slot="option"
                  slot-scope="props"
                >
                  <div class="multiselect-option">
                    <strong>{{ props.option.display_name }}</strong>
                    <small class="text-muted d-block">{{ props.option.product_list_name }}</small>
                  </div>
                </template>
                <template slot="noResult">
                  No products found
                </template>
              </Multiselect>
            </div>
          </div>
        </div>

        <div class="divider-with-text mb-3">
          <span>OR</span>
        </div>

        <div class="d-flex justify-content-between align-items-center mb-2">
          <label class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mb-0">
            Amazon ASINs, Walmart IDs, or Product URLs
          </label>

          <select
            v-model="selectedCountry"
            class="form-control form-control-sm"
            style="max-width: 220px;"
          >
            <option
              v-for="country in amazonCountries"
              :key="country.value"
              :value="country.value"
            >
              {{ country.label }}
            </option>
          </select>
        </div>

        <textarea
          v-model="urls"
          placeholder="Enter one per line: B08XYZ1234, https://www.amazon.com/dp/B08ABC5678"
        />

        <div class="row mx-0">
          <div class="col-md-6 pl-0 pr-2">
            <label
              class="text-sm font-medium leading-none"
              for="used-keywords"
            >
              Keywords <span class="text-muted font-weight-normal">(optional)</span>
            </label>
            <textarea
              id="used-keywords"
              v-model="keywords"
              class="mt-1"
              placeholder="Preferred keywords (optional). Separate with commas."
            />
          </div>
          <div class="col-md-6 pl-2 pr-0">
            <label
              class="text-sm font-medium leading-none"
              for="excluded-keywords"
            >
              Excluded Keywords <span class="text-muted font-weight-normal">(optional)</span>
            </label>
            <textarea
              id="excluded-keywords"
              v-model="excludedKeywords"
              class="mt-1"
              placeholder="Excluded keywords (optional). Separate with commas."
            />
          </div>
        </div>
      </div>
    </section>
    <section 
      v-if="selectedSource === 'spreadsheet' && !selectedReport && selectedPromptId !== 4" 
      class="drop-zone source-sections mt-3"
    >
      <Vue2Dropzone
        id="imageUploadDropzone"
        ref="fileInput"
        key="imageUploadDropzone"
        :class="{ 'has-files': uploadedFiles.length > 0 }"
        :options="dropzoneOptions"
        @vdropzone-file-added="onFileAdded"
        @vdropzone-removed-file="onFileRemoved"
      />

      <p
        v-if="sourceError"
        class="text-muted"
      >
        Product ID or ASIN column is required.
      </p>

      <div
        v-if="uploadedFiles.length > 0"
        class="uploaded-files-list mt-3"
      >
        <div
          v-for="item in uploadedFiles"
          :key="item.id"
          class="uploaded-file-item"
        >
          <div class="uploaded-file-row">
            <div class="uploaded-file-info">
              <i class="bi bi-upload uploaded-file-icon" />
              <strong>{{ item.name }}</strong>
              <span class="uploaded-file-size">{{ formatFileSize(item.size) }}</span>
            </div>
            <button
              type="button"
              class="btn-close-file"
              aria-label="Remove"
              @click="removeUploadedFile(item)"
            >
              <i class="bi bi-x-lg" />
            </button>
          </div>
          <div class="uploaded-file-save">
            <label class="save-as-list-label">
              <input
                v-model="item.saveAsList"
                type="checkbox"
              >
              <span class="save-as-list-text">Save as list:</span>
            </label>
            <input
              v-model="item.listName"
              type="text"
              class="form-control form-control-sm save-as-list-input"
              :disabled="!item.saveAsList"
            >
          </div>

          <div
            v-if="item.sheetOptions && item.sheetOptions.length > 1"
            class="uploaded-file-sheet mt-2"
          >
            <label class="uploaded-file-sheet__label">
              Sheet:
            </label>

            <select
              v-model="item.selectedSheetName"
              class="form-control form-control-sm uploaded-file-sheet__select"
              @change="handleSpreadsheetSheetChange(item)"
            >
              <option
                v-for="sheetName in item.sheetOptions"
                :key="sheetName"
                :value="sheetName"
              >
                {{ sheetName }}
              </option>
            </select>
          </div>

          <div
            v-if="item.hasAutoFilter"
            class="uploaded-file-filter mt-2"
          >
            <label class="uploaded-file-filter__label">
              <input
                v-model="item.selectAllSpreadsheetRows"
                type="checkbox"
                @change="handleSpreadsheetSelectAllChange(item)"
              >
              <span>Select all rows</span>
            </label>

            <span class="uploaded-file-filter__hint">
              Off by default: uses only visible / filtered rows.
            </span>
          </div>
        </div>
      </div>
    </section>
    <section 
      v-if="selectedSource === 'salsify_integration' && !selectedReport && selectedPromptId !== 4" 
      class="salsify-integration source-sections mt-3"
    >
      <section
        class="section"
        aria-labelledby="push-title"
      >
        <h2 id="push-title">
          <span
            class="status-dot"
            aria-hidden="true"
          /> 
          Push Mode – Webhook
        </h2>
        <p class="lead text-muted">
          Configure Salsify to automatically push product updates to this webhook URL:
        </p>

        <div
          class="input-row"
          role="group"
          aria-label="Webhook URL"
        >
          <input
            id="webhook-url"
            type="text"
            readonly
            value="https://id-preview--1b3de5e7-06d8-4843-b250-14349dea299a.rapidads.io/api/salsify-webhook"
          >
          <button
            id="copy-url"
            class="btn ghost"
            type="button"
            aria-label="Copy URL"
          >
            Copy
          </button>
        </div>

        <p class="mini-note text-muted">
          In Salsify, go to Settings → Webhooks → Add Webhook and paste this URL
        </p>
      </section>

      <section
        class="section"
        aria-labelledby="pull-title"
      >
        <h2 id="pull-title">
          <span
            class="status-dot"
            aria-hidden="true"
          /> 
          Pull Mode – Fetch from Salsify
        </h2>

        <div
          class="tabs"
          role="tablist"
          aria-label="Select input method"
        >
          <button
            id="tab-ids"
            class="tab"
            role="tab"
            aria-controls="pane-ids"
            aria-selected="true"
            :class="{ active: pullMode === 'manual' }"
            @click="pullMode = 'manual'"
          >
            Manual IDs
          </button>
          <button
            id="tab-list"
            class="tab"
            role="tab"
            aria-controls="pane-list"
            aria-selected="false"
            :class="{ active: pullMode === 'product_list' }"
            @click="pullMode = 'product_list'"
          >
            Product List
          </button>
        </div>

        <div class="tabpanes">
          <div
            v-if="pullMode === 'manual'"
            id="pane-ids"
            class="tabpane active"
            role="tabpanel"
            aria-labelledby="tab-ids"
          >
            <div class="form-row">
              <label for="ids">Salsify Product IDs</label>
              <textarea
                id="ids"
                placeholder="Enter Salsify product IDs (one per line)
12345
67890
24680"
              />
              <div class="help text-muted">
                We'll fetch product data from Salsify API
              </div>
            </div>
          </div>

          <div
            v-else
            id="pane-list"
            class="tabpane"
            role="tabpanel"
            aria-labelledby="tab-list"
          >
            <div class="form-row">
              <label for="product-list">Select Product List</label>
              <select id="product-list">
                <option
                  value=""
                  selected
                  disabled
                >
                  Choose a product list…
                </option>
                <option>Q4 2025 Products (24 products)</option>
                <option>Summer Collection (18 products)</option>
                <option>Best Sellers (32 products)</option>
              </select>
              <div class="help">
                Products from your saved Salsify lists
              </div>
            </div>
          </div>
        </div>
      </section>
    </section>
    <div 
      v-if="selectedSource && !selectedReport && selectedPromptId !== 4" 
      class="d-flex justify-content-end align-content-end mt-4"
    >
      <button 
        class="btn continue-btn" 
        :disabled="isProcessing || isErrors"
        @click="prepareReport"
      >
        <span v-if="!isProcessing">Continue to Analysis</span>
        <span v-else>Processing...</span>
        <i class="bi bi-arrow-right d-flex ml-2" />
      </button>
    </div>
    
    
    <section
      v-if="(selectedReport && reportData) || selectedPromptId === 4"
      class="pdp-tab-section"
    >
      <!--    analysis   -->
      <div
        v-if="selectedPromptId === 1"
      >
        <PdpTextCopyTab
          :report-data="reportData"
          :analysis-content="titleCopyAnalysisContent"
          :report-content="titleCopyReportContent"
          :selected-prompt-value="selectedTitleCopyPromptValue"
          :selected-prompt-label="selectedTitleCopyPromptOption.label"
          :is-regenerating="selectedReport && updatingReports.includes(selectedReport.id)"
          @regenerate="regenerateReport"
        />
      </div>
      <div
        v-if="selectedPromptId === 2"
        class="optimize-report"
        :class="{ 'shared-locked': isTabLocked(2) }"
      >
        <div class="analysis-summary-accordion mb-4">
          <div
            class="accordion-header"
            @click="toggleAnalysisSummary"
          >
            <div class="accordion-header-content">
              <h5 class="accordion-title">
                Analysis Summary
              </h5>
              <i
                class="bi accordion-icon"
                :class="isAnalysisSummaryExpanded ? 'bi-chevron-down' : 'bi-chevron-right'"
              />
            </div>
          </div>
          <div
            v-show="isAnalysisSummaryExpanded"
            class="accordion-body"
          >
            <div class="score-wrap">
              <div class="score-card">
                <span class="text-muted">Current Score</span>
                <div class="score">
                  <span class="current">{{ reportData?.analysis_content?.score || 0 }}</span> / <span>100</span>
                </div>
                <div class="progress">
                  <div
                    class="progress-bar bg-success"
                    role="progressbar"
                    :style="{width: (reportData?.analysis_content?.score ?? 0) + '%'}"
                    :aria-valuenow="reportData?.analysis_content?.score ?? 0"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  />
                </div>
              </div>

              <div class="score-card">
                <span class="text-muted">Issues Found</span>
                <p class="issues-count">
                  {{ reportData?.analysis_content?.issues_found?.total ?? '-' }}
                </p>
                <div class="badges-list">
                  <div
                    v-if="reportData?.analysis_content?.issues_found?.critical"
                    class="badge badge-danger"
                  >
                    {{ reportData?.analysis_content?.issues_found?.critical }} Critical
                  </div>
                  <div
                    v-if="reportData?.analysis_content?.issues_found?.warning"
                    class="badge badge-warning"
                  >
                    {{ reportData?.analysis_content?.issues_found?.warning }} Warning
                  </div>
                  <div
                    v-if="reportData?.analysis_content?.issues_found?.info"
                    class="badge badge-info"
                  >
                    {{ reportData?.analysis_content?.issues_found?.info }} Info
                  </div>
                </div>
              </div>

              <div class="score-card">
                <span class="text-muted">Potential Gain</span>
                <div class="gains-card">
                  <div class="gains-item">
                    <span>+ {{ reportData?.analysis_content?.potential_gain ?? '-' }}</span> points
                  </div>
                  <span class="text-muted">After optimization</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Render HTML -->
        <div
          ref="optRoot"
          class="opt-root mb-4"
          @click.capture="onRootClick"
          @input="onRootInput"
          v-html="reportData.optimize_content"
        />

        <!-- Bottom toolbar (shows Save/Cancel only when editing) -->
        <div
          v-if="isEditing"
          class="d-flex align-items-center gap-2 pdp-edit-toolbar"
        >
          <button
            :class="(isEditingKeywords || isEditingExcludedKeywords) ? 'btn btn-outline-secondary btn-sm' : 'btn btn-success btn-sm'"
            :disabled="isSaving"
            @click="saveOptimized"
          >
            {{ isSaving ? 'Saving...' : 'Save' }}
          </button>

          <button
            v-if="isEditingKeywords || isEditingExcludedKeywords"
            class="btn btn-success btn-sm"
            :disabled="isSaving"
            @click="saveAndRegenerate"
          >
            {{ isSaving ? 'Saving...' : 'Save & Re-Generate Report' }}
          </button>

          <button
            class="btn btn-link btn-sm text-muted px-1"
            :disabled="isSaving"
            @click="cancelEdit"
          >
            Cancel
          </button>

          <small class="text-muted ms-2">
            Editing in progress…
          </small>
        </div>
      </div>
      <div
        v-if="selectedPromptId === 3"
        class="kw-rankings"
        :class="{ 'shared-locked': isTabLocked(3) }"
      >
        <div class="kw-card">
          <div class="kw-head">
            <h4 class="mb-1">
              Keyword Rankings
            </h4>
            <div class="text-muted">
              Current keyword performance comparison
            </div>
            <div class="button-list mt-3">
              <button 
                class="btn" 
                @click="showKeywordForm = !showKeywordForm"
              >
                <i class="bi bi-plus" /> Add Keyword
              </button>
              <button 
                class="btn" 
                @click="reCrawlKeywords"
              >
                <i class="bi bi-arrow-clockwise" /> Re-run Crawl
              </button>
            </div>
            <div 
              v-if="showKeywordForm"
              class="add-form d-flex align-items-center gap-2 mt-3"
            >
              <input 
                v-model="newKeyword"
                type="text" 
                class="form-control keyword-input" 
                placeholder="Enter keyword..."
              >
              <button 
                type="button" 
                class="btn add-btn ml-2"
                @click="addKeyword"
              >
                Add
              </button>
            </div>
          </div>

          <div class="kw-kpis">
            <div class="kpi">
              <div class="kpi-label text-muted">
                Keywords Tracked
              </div>
              <div class="kpi-value">
                {{ reportData?.keywords?.length ?? 0 }}
              </div>
            </div>
            <div class="kpi">
              <div class="kpi-label text-muted">
                Avg. Rank Before
              </div>
              <div class="kpi-value">
                {{ reportData?.analysis_content?.keyword_rankings?.avg_before ?? 0 }}
              </div>
            </div>
            <div class="kpi">
              <div class="kpi-label text-muted">
                Avg. Rank After
              </div>
              <div class="kpi-value text-success">
                {{ reportData?.analysis_content?.keyword_rankings?.avg_after ?? 0 }}
              </div>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table kw-table mb-0">
              <thead>
                <tr>
                  <th>Keyword</th>
                  <th class="text-center">
                    Before
                  </th>
                  <th class="text-center">
                    After
                  </th>
                  <th class="text-center">
                    Change
                  </th>
                  <th class="text-end">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, i) in reportKeywords"
                  :key="'kw-'+i"
                >
                  <td class="kw-text">
                    {{ row.keyword }}
                  </td>
                  <td class="text-center">
                    {{ row.start_position }}
                  </td>
                  <td class="text-center text-success fw-semibold">
                    {{ row.current_position }}
                  </td>
                  <td class="text-center">
                    <span 
                      v-if="!isNaN(+row.current_position) && Math.max(0, (!isNaN(+row.start_position) ? +row.start_position : 0) - (!isNaN(+row.current_position) ? +row.current_position : 0)) > 0" 
                      class="change-pill"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M7 17L17 7M17 7H9M17 7V15"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                      +{{ Math.max(0, (!isNaN(+row.start_position) ? +row.start_position : 0) - (!isNaN(+row.current_position) ? +row.current_position : 0)) }}
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td class="text-end">
                    <i 
                      class="bi bi-trash delete-keyword" 
                      @click="deleteKeyword(row)" 
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div
        v-if="selectedPromptId === 4"
        :class="{ 'shared-locked': isTabLocked(4) }"
      >
        <ShareOfShelf v-if="!isTabLocked(4)" />
        <div
          v-else
          class="shared-locked-placeholder"
        />
      </div>
      <div v-if="selectedPromptId === 5">
        <ImageAnalysisOverview
          :images="selectedReport?.images ?? []"
          :pdp-report="reportData"
          :analysis-content="imagesAnalysisContent"
          :report-content="imagesReportContent"
          :selected-prompt-value="selectedImagesPromptValue"
          :selected-prompt-label="selectedImagePromptOption.label"
          :is-regenerating="updatingReports.includes(selectedReport.id)"
          :read-only="isShared && !isAuthenticated"
          @regenerate="regenerateReportImages"
        />
      </div>

      <div
        v-if="isTabLocked(selectedPromptId)"
        class="shared-lock-overlay"
      >
        <div class="shared-lock-card">
          <i class="bi bi-lock-fill shared-lock-icon" />
          <p class="shared-lock-text mb-3">
            This section is only available to logged-in users.
          </p>
          <a
            :href="loginUrl"
            class="btn btn-primary"
          >
            Login to view the full report
          </a>
        </div>
      </div>
    </section>

    <OptixPublishToPlatformModal
      v-if="!(isShared && !isAuthenticated)"
      :report="selectedReport"
      :optimized-content="optimizedContent"
    />
  </div>
</template>

<script>
import Vue2Dropzone from "vue2-dropzone";
import Multiselect from 'vue-multiselect';
import ClickOutside from 'vue-click-outside';
import { ChartPie } from 'lucide-vue';
import * as XLSX from 'xlsx';
import ShareOfShelf from "@frontend/components/common/dashboard/ShareOfShelf.vue";
import ImageAnalysisOverview from "@frontend/components/common/dashboard/ImageAnalysisOverview.vue";
import OptixPublishToPlatformModal from "@frontend/components/modules/file-browser/modals/OptixPublishToPlatformModal";
import PdpTextCopyTab from "@frontend/components/common/dashboard/pdp-tabs/PdpTextCopyTab.vue";
const CUSTOM_PROMPT_TYPE_ANALYTICS = 3;
const CUSTOM_PROMPT_TYPE_IMAGE_REORDER = 6;
const CUSTOM_PROMPT_TYPE_IMAGE_COMPARISON = 7;
const PDP_REPORT_CONTENT_TYPE_TITLE_COPY = 1;
const PDP_REPORT_CONTENT_TYPE_IMAGES = 2;
const DEFAULT_PROMPT_VALUE = 'default';

  export default {
    name: 'PDPAnalysis',
    components: {
      PdpTextCopyTab,
      ImageAnalysisOverview,
      Vue2Dropzone,
      Multiselect,
      ChartPie,
      ShareOfShelf,
      OptixPublishToPlatformModal
    },
    directives: {
      ClickOutside
    },
    beforeRouteLeave(to, from, next) {
      if (this.isDirty) {
        const answer = window.confirm('You have unsaved changes! Are you sure you want to leave?');
        if (answer) {
          next();
        } else {
          next(false);
        }
      } else {
        next();
      }
    },
    props: {
      reports: {
        type: Array,
        default: () => []
      },
      userId: {
        type: Number,
        default: 0,
      },
      companyId: {
        type: Number,
        default: 0,
      },
      imageTab: {
        type: Number,
        default: 0,
      },
      isShared: {
        type: Boolean,
        default: false,
      },
      isAuthenticated: {
        type: Boolean,
        default: true,
      },
      selectedReportId: {
        type: Number,
        default: null,
      },
      loginUrl: {
        type: String,
        default: '/login',
      },
    },
    data() {
      return {
        prompts: [],
        pdpTabs: [
          {
            id: 1,
            title: 'Title + Copy',
            icon: 'bi bi-stars',
            icon_type: 'string'
          },
          {
            id: 2,
            title: 'Optimize',
            icon: 'bi bi-magic',
            icon_type: 'string'
          },
          {
            id: 5,
            title: 'Images',
            icon: 'bi bi-card-image',
            icon_type: 'string'
          },
          {
            id: 3,
            title: 'Keywords',
            icon: 'bi bi-search',
            icon_type: 'string'
          },
          {
            id: 4,
            title: 'Share of Shelf',
            icon: ChartPie,
            icon_type: 'component'
          }
        ],
        selectedPromptId: null,
        selectedSource: null,
        sources: [
          {
            id: 'asin_urls',
            title: 'ASINs/URLs',
            icon: 'bi bi-link-45deg',
            description: 'Enter Amazon ASINs, Walmart IDs, or product URLs directly'
          },
          {
            id: 'spreadsheet',
            title: 'Spreadsheet Upload',
            icon: 'bi bi-file-earmark-spreadsheet',
            description: 'Upload an XLSX, XLS, or CSV file with product data'
          },
          {
            id: 'salsify_integration',
            title: 'Salsify Integration',
            icon: 'bi bi-cloud-arrow-up',
            description: 'Connect directly to your Salsify product catalog'
          }
        ],
        dropzoneOptions: {
          url: "http",
          autoProcessQueue: false,
          addRemoveLinks: true,
          createImageThumbnails: true,
          acceptedFiles: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel.sheet.macroEnabled.12, text/csv",
          maxFiles: null,
          parallelUploads: 10,
          uploadMultiple: true,
          maxThumbnailFilesize: 50,
          dictDefaultMessage:
            '<div class="dz-upload-icon" style="margin-bottom:10px;line-height:1;"><i class="bi bi-upload" style="font-size:28px;line-height:1;display:inline-block;"></i></div><div class="dz-upload-title"><span class="dz-title-empty">Drop your files here or click to browse</span><span class="dz-title-has">Add more files</span></div><div class="dz-upload-sub">Supports CSV, XLSX, and XLS <i class="bi bi-info-circle" title="Expected columns:&#10;• ASIN or Product ID&#10;• Product Name&#10;• Title&#10;• Description&#10;• Bullet Points&#10;• Keywords (optional)"></i></div>',
          dictMaxFilesExceeded: null,
        },
        pullMode: 'manual',
        urls: '',
        excludedKeywords: '',
        keywords: '',
        hideSource: false,
        isProcessing: false,
        selectedReport: null,
        reportSearchQuery: '',
        isReportDropdownOpen: false,
        isReportHistoryOpen: false,
        historyScope: 'product',
        historyDateFilter: 'any',
        historySpecificDate: null,
        historyUserFilter: null,
        isDateFilterOpen: false,
        isDatePickerOpen: false,
        isUserFilterOpen: false,
        datePickerViewDate: new Date(),
        dateFilterOptions: [
          { value: 'any', label: 'Any time' },
          { value: '7', label: 'Last 7 days' },
          { value: '30', label: 'Last 30 days' },
          { value: '90', label: 'Last 90 days' },
          { value: 'specific', label: 'Specific date...' },
        ],
        deletedReports: [],
        addedReports: [],
        updatedReports: [],
        updatingReports: [],
        newKeyword: '',
        showKeywordForm: false,
        isAnalysisSummaryExpanded: true,
        isExecutiveSummaryExpanded: false,
        isQuantitativeAnalysisExpanded: false,
        isRecommendationsExpanded: false,
        isSummaryExpanded: false,
        sourceSpreadSheetArr: {},
        uploadedFiles: [],
        isErrors: true,
        sourceError: false,
        isAddProductsExpanded: false,
        isProductListExpanded: false,
        selectedProductLists: [],
        availableProductLists: [],
        selectedProducts: [],
        availableProducts: [],
        isLoadingProductLists: false,
        isLoadingProducts: false,
        productTypeFilter: 'all',
        lightbox: {
          isOpen: false,
          src: null,
        },
        isEditing: false,
        isSaving: false,
        originalHtml: '',
        activeBoxEl: null,
        isDirty: false,
        sortMode: 'alpha',
        isEditingKeywords: false,
        editingKeywords: [],
        keywordBeforeBoxEl: null,
        originalBeforeKeywordsHtml: '',
        isEditingExcludedKeywords: false,
        editingExcludedKeywords: [],
        excludedKeywordBeforeBoxEl: null,
        originalBeforeExcludedKeywordsHtml: '',
        promptSettingsTab: 'title_copy',
        promptSearchQuery: '',
        selectedCountry: 'US',
        amazonCountries: [
          {
            value: 'US',
            label: 'United States',
            domain: 'www.amazon.com',
            locale: 'en-US',
            language: 'en_US'
          },
          {
            value: 'UK',
            label: 'United Kingdom',
            domain: 'www.amazon.co.uk',
            locale: 'en-GB',
            language: 'en_GB'
          },
          {
            value: 'DE',
            label: 'Germany',
            domain: 'www.amazon.de',
            locale: 'de-DE',
            language: 'de_DE'
          },
          {
            value: 'FR',
            label: 'France',
            domain: 'www.amazon.fr',
            locale: 'fr-FR',
            language: 'fr_FR'
          },
          {
            value: 'IT',
            label: 'Italy',
            domain: 'www.amazon.it',
            locale: 'it-IT',
            language: 'it_IT'
          },
          {
            value: 'ES',
            label: 'Spain',
            domain: 'www.amazon.es',
            locale: 'es-ES',
            language: 'es_ES'
          },
          {
            value: 'NL',
            label: 'Netherlands',
            domain: 'www.amazon.nl',
            locale: 'nl-NL',
            language: 'nl_NL'
          },
          {
            value: 'SE',
            label: 'Sweden',
            domain: 'www.amazon.se',
            locale: 'sv-SE',
            language: 'sv_SE'
          },
          {
            value: 'PL',
            label: 'Poland',
            domain: 'www.amazon.pl',
            locale: 'pl-PL',
            language: 'pl_PL'
          },
          {
            value: 'JP',
            label: 'Japan',
            domain: 'www.amazon.co.jp',
            locale: 'ja-JP',
            language: 'ja_JP'
          },
          {
            value: 'CA',
            label: 'Canada',
            domain: 'www.amazon.ca',
            locale: 'en-CA',
            language: 'en_CA'
          }
        ],
      }
    },
    computed: {
      selectedTitleCopyPromptValue() {
        return this.$store.state.pdpPromptSettings?.selectedTitlesPrompt || DEFAULT_PROMPT_VALUE;
      },

      selectedImagesPromptValue() {
        return this.$store.state.pdpPromptSettings?.selectedImagesPrompt || DEFAULT_PROMPT_VALUE;
      },

      activeReportContentType() {
        if (this.selectedPromptId === 5) {
          return PDP_REPORT_CONTENT_TYPE_IMAGES;
        }

        return PDP_REPORT_CONTENT_TYPE_TITLE_COPY;
      },

      activeSelectedPromptValue() {
        if (this.activeReportContentType === PDP_REPORT_CONTENT_TYPE_IMAGES) {
          return this.selectedImagesPromptValue;
        }

        return this.selectedTitleCopyPromptValue;
      },
      titleCopyReportContent() {
        return this.findReportContent({
          report: this.reportData,
          type: PDP_REPORT_CONTENT_TYPE_TITLE_COPY,
          promptValue: this.selectedTitleCopyPromptValue,
        });
      },

      imagesReportContent() {
        return this.findReportContent({
          report: this.reportData,
          type: PDP_REPORT_CONTENT_TYPE_IMAGES,
          promptValue: this.selectedImagesPromptValue,
        });
      },

      activeReportContent() {
        return this.findReportContent({
          report: this.reportData,
          type: this.activeReportContentType,
          promptValue: this.activeSelectedPromptValue,
        });
      },

      titleCopyAnalysisContent() {
        return this.normalizeReportContent(this.titleCopyReportContent?.content);
      },

      imagesAnalysisContent() {
        return this.normalizeReportContent(this.imagesReportContent?.content);
      },

      analysisContent() {
        return this.normalizeReportContent(this.activeReportContent?.content);
      },
      promptOptions() {
        const options = this.$store.getters['pdpPromptSettings/promptOptions'];

        return options && options.length
          ? options
          : [this.getDefaultPromptOption()];
      },

      selectedImagePromptOption() {
        return this.$store.getters['pdpPromptSettings/selectedImagesPromptOption']
          || this.getDefaultPromptOption();
      },

      selectedTitleCopyPromptOption() {
        return this.$store.getters['pdpPromptSettings/selectedTitlesPromptOption']
          || this.getDefaultPromptOption();
      },

      selectedImagePromptType() {
        return this.$store.state.pdpPromptSettings?.selectedImagesPrompt || 'default';
      },

      selectedTitleCopyPromptType() {
        return this.$store.state.pdpPromptSettings?.selectedTitlesPrompt || 'default';
      },

      activePromptSettingsValue() {
        return this.promptSettingsTab === 'images'
          ? this.selectedImagePromptType
          : this.selectedTitleCopyPromptType;
      },

      activePromptSettingsOption() {
        return this.promptOptions.find(
          option => option.value === this.activePromptSettingsValue
        ) || this.getDefaultPromptOption();
      },

      filteredPromptOptions() {
        const query = (this.promptSearchQuery || '').toLowerCase().trim();

        if (!query) {
          return this.promptOptions;
        }

        return this.promptOptions.filter((option) => {
          const label = (option.label || '').toLowerCase();
          const description = (option.description || '').toLowerCase();
          const value = String(option.value || '').toLowerCase();

          return (
            label.includes(query) ||
            description.includes(query) ||
            value.includes(query)
          );
        });
      },
      selectedCountryConfig() {
        return this.amazonCountries.find(item => item.value === this.selectedCountry)
          || this.amazonCountries[0];
      },
      isIframe() {
        return window.self !== window.top
      },
      lockedTabIds() {
        return [2, 3, 4];
      },
      mergedReports() {
        const all = [...this.reports, ...this.addedReports];
        return all.map(report => {
          if (this.updatedReports.length) {
            const updated = this.updatedReports.find(item => item.id === report.id);
            if (updated) return { ...report, ...updated };
          }
          return report;
        });
      },
      reportList() {
        const allReports = this.mergedReports.filter(item => !this.deletedReports.includes(item.id));

        // Deduplicate by URL: keep one report per URL
        // Prefer the latest completed report; only show "Processing..." if no completed report exists
        const latestByUrl = {};
        for (const report of allReports) {
          const existing = latestByUrl[report.url];
          if (!existing) {
            latestByUrl[report.url] = report;
          } else {
            const existingIsProcessing = existing.product_name === 'Processing...';
            const currentIsProcessing = report.product_name === 'Processing...';

            if (existingIsProcessing && !currentIsProcessing) {
              latestByUrl[report.url] = report;
            } else if (!existingIsProcessing && currentIsProcessing) {
              // Keep the completed report, skip the processing one
            } else {
              if (report.id > existing.id) {
                latestByUrl[report.url] = report;
              }
            }
          }
        }

        const reports = Object.values(latestByUrl).map(report => {
          const productId = this.getId(report.url);
          return {
            id: report.id,
            productId,
            productName: report.product_name,
            name: productId + ' - ' + report.product_name,
            url: report.url,
            images: report.images,
            image_content: report.image_content,
            image_stat: report.image_stat,
            score: this.getReportScore(
              report,
              PDP_REPORT_CONTENT_TYPE_TITLE_COPY,
              this.selectedTitleCopyPromptValue
            )
          }
        });

        if (this.sortMode === 'alpha') {
          reports.sort((a, b) => a.name.localeCompare(b.name));
        } else if (this.sortMode === 'score_desc') {
          reports.sort((a, b) => b.score - a.score);
        } else if (this.sortMode === 'score_asc') {
          reports.sort((a, b) => a.score - b.score);
        }

        return reports;
      },
      filteredReportList() {
        if (!this.reportSearchQuery) return this.reportList;
        const q = this.reportSearchQuery.toLowerCase();
        return this.reportList.filter(r =>
          r.productId.toLowerCase().includes(q) ||
          r.productName.toLowerCase().includes(q)
        );
      },
      dateFilterLabel() {
        const opt = this.dateFilterOptions.find(o => o.value === this.historyDateFilter);
        return opt ? opt.label : 'Any time';
      },
      historyUserNames() {
        const names = new Set();
        this.reports.forEach(r => {
          const name = this.getReportUserName(r);
          if (name) names.add(name);
        });
        return [...names].sort();
      },
      datePickerMonthLabel() {
        const d = this.datePickerViewDate;
        return d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
      },
      reportDateStrings() {
        // Build a set of date strings (toDateString) that have completed reports
        const dates = new Set();
        const list = this.mergedReports.filter(item =>
          !this.deletedReports.includes(item.id) && item.product_name !== 'Processing...'
        );
        // Apply scope filter (this product vs all) and user filter
        const scoped = list.filter(r => {
          if (this.historyScope === 'product' && this.selectedReport && r.url !== this.selectedReport.url) return false;
          if (this.historyUserFilter && this.getReportUserName(r) !== this.historyUserFilter) return false;
          return true;
        });
        for (const r of scoped) {
          if (r.created_at) dates.add(new Date(r.created_at).toDateString());
        }
        return dates;
      },
      datePickerDays() {
        const year = this.datePickerViewDate.getFullYear();
        const month = this.datePickerViewDate.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const daysInPrevMonth = new Date(year, month, 0).getDate();
        const today = new Date();
        const availableDates = this.reportDateStrings;
        const days = [];

        for (let i = firstDay - 1; i >= 0; i--) {
          const d = daysInPrevMonth - i;
          const date = new Date(year, month - 1, d);
          days.push({ day: d, currentMonth: false, date, isSelected: false, isToday: false, hasReport: availableDates.has(date.toDateString()) });
        }

        for (let d = 1; d <= daysInMonth; d++) {
          const date = new Date(year, month, d);
          const isSelected = this.historySpecificDate &&
            date.toDateString() === this.historySpecificDate.toDateString();
          const isToday = date.toDateString() === today.toDateString();
          days.push({ day: d, currentMonth: true, date, isSelected, isToday, hasReport: availableDates.has(date.toDateString()) });
        }

        const remaining = 42 - days.length;
        for (let d = 1; d <= remaining; d++) {
          const date = new Date(year, month + 1, d);
          days.push({ day: d, currentMonth: false, date, isSelected: false, isToday: false, hasReport: availableDates.has(date.toDateString()) });
        }

        return days;
      },
      filteredHistoryList() {
        let list = [...this.mergedReports].filter(item => !this.deletedReports.includes(item.id));

        // Scope: this product vs all
        if (this.historyScope === 'product' && this.selectedReport) {
          const selectedUrl = this.selectedReport.url;
          list = list.filter(r => r.url === selectedUrl);
        }

        // Date filter
        if (this.historyDateFilter !== 'any') {
          if (this.historyDateFilter === 'specific' && this.historySpecificDate) {
            const picked = this.historySpecificDate.toDateString();
            list = list.filter(r => new Date(r.created_at).toDateString() === picked);
          } else {
            const days = parseInt(this.historyDateFilter);
            if (!isNaN(days)) {
              const cutoff = new Date();
              cutoff.setDate(cutoff.getDate() - days);
              list = list.filter(r => new Date(r.created_at) >= cutoff);
            }
          }
        }

        // User filter
        if (this.historyUserFilter) {
          list = list.filter(r => this.getReportUserName(r) === this.historyUserFilter);
        }

        // Newest first
        list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        return list;
      },
      lastAnalyzedAt() {
        if (!this.selectedReport) return null;
        const url = this.selectedReport.url;
        const completed = this.mergedReports.filter(r =>
          r.url === url &&
          !this.deletedReports.includes(r.id) &&
          r.product_name !== 'Processing...' &&
          r.created_at
        );
        if (!completed.length) return null;
        completed.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        return completed[0].created_at;
      },
      reportData() {
        if (!this.selectedReport) return null;

        if (this.updatedReports.length && this.updatedReports.find(item => item.id === this.selectedReport.id)) {
          return this.updatedReports.find(item => item.id === this.selectedReport.id);
        }

        return this.reports.find(report => report.id === this.selectedReport.id)
          || this.addedReports.find(report => report.id === this.selectedReport.id);
      },
      reportKeywords() {
        if (!this.reportData?.keywords || Array.isArray(this.reportData?.keywords) === false) return [];
        let keywords = this.reportData.keywords;
        return keywords.sort((a, b) => {
          const parsePos = (val) => {
            if (val == null) return Infinity;
            if (typeof val === 'number') return val;
            if (typeof val === 'string') {
              const num = parseInt(val.replace(/[^\d]/g, ''), 10);
              return isNaN(num) ? Infinity : num;
            }
            return Infinity;
          };

          const posA = parsePos(a.start_position);
          const posB = parsePos(b.start_position);

          return posA - posB;
        });
      },

      optimizedContent() {
        if (!this.reportData?.optimize_content) {
          return null;
        }

        const doc = new DOMParser().parseFromString(this.reportData.optimize_content, 'text/html');

        const result = { title: '', bullets: '', description: '', keywords: (this.reportData.keywords || []).map(k => k.keyword) };

        doc.querySelectorAll('div.small.text-success').forEach(label => {
          if (!label.textContent.includes('After')) {
            return;
          }

          const box = label.parentElement;

          if (!box) {
            return;
          }

          const moduleTitle = this.getModuleTitleForNode(label);

          const text = Array.from(box.childNodes)
            .filter(n => n !== label)
            .map(n => n.textContent)
            .join('').trim();

          if (moduleTitle === 'Product Title') {
            result.title = text;
          } else if (moduleTitle === 'Bullet Points') {
            result.bullets = text.split('\n').map(b => b.trim()).filter(Boolean);
          } else if (moduleTitle === 'Product Description') {
            result.description = text;
          }
        });

        return result;
      },
      productListOptions() {
        return this.availableProductLists.map(list => ({
          id: list.id,
          name: list.name
        }));
      },
      availableProductTypes() {
        const types = new Set();
        this.availableProducts.forEach(product => {
          if (product.asin) types.add('ASIN');
          else if (product.tcin) types.add('TCIN');
          else if (product.walmart_id) types.add('Walmart ID');
          else if (product.salsify_id) types.add('Salsify ID');
          else if (product.url) types.add('URL');
        });
        return Array.from(types);
      },
      filteredProducts() {
        if (this.productTypeFilter === 'all') {
          return this.availableProducts;
        }
        return this.availableProducts.filter(product => {
          if (this.productTypeFilter === 'ASIN') return !!product.asin;
          if (this.productTypeFilter === 'TCIN') return !!product.tcin;
          if (this.productTypeFilter === 'Walmart ID') return !!product.walmart_id;
          if (this.productTypeFilter === 'Salsify ID') return !!product.salsify_id;
          if (this.productTypeFilter === 'URL') return !!product.url;
          return true;
        });
      },

      scoreValue() {
        const content = this.analysisContent;

        if (content && typeof content === 'object' && content.score != null) {
          return content.score;
        }

        if (this.selectedReport?.score != null) {
          return this.selectedReport.score;
        }

        return 0;
      },
    },
    watch: {
      urls: function (val) {
        this.updateIsErrors();
      },
      selectedProducts: function (val) {
        this.updateIsErrors();
      },
      selectedReport: function (val) {
        this.updateUrlWithProduct(val);
      },
      'reportData.optimize_content': {
        handler() {
          this.$nextTick(() => this.decorateAfterBlocksAlways());
        },
        immediate: true,
      },
      selectedCountry: function (val) {
        this.normalizeUrlsTextarea()
      }
    },
    created() {
      if (this.isShared || !window.Echo || !this.companyId) {
        return;
      }
      window.Echo.channel(`pdp_reports.${this.companyId}`)
        .listen('.pdp.analysis.completed', (e) => {
          if (e.data?.report) {
            this.updateReport(e.data.report);
          }

          if (e.data?.keyword) {
            this.updateKeyword(e.data.keyword);
          }
        });
    },
    async mounted() {
      this.$nextTick(() => {
        $(document).on('click', '#pdp-analysis-tab', (e) => {
          this.autoSelectReport()
        });
      });
      this.selectedPromptId = this.pdpTabs[0].id;
      if (this.isShared && this.selectedReportId) {
        const r = this.reports.find(item => +item.id === +this.selectedReportId) || this.reports[0];
        if (r) {
          const pid = this.getId(r.url);
          this.selectedReport = {
            id: r.id,
            productId: pid,
            productName: r.product_name,
            name: pid + ' - ' + r.product_name,
          };
        }
      } else if ((window.location.hash && window.location.hash.includes('#pdp-analysis'))
        || (window.location.search && window.location.search.includes('product='))) {
        this.autoSelectReport();
      }

      if (!this.isShared) {
        this.loadAvailableProductLists();
      }
      this.decorateAfterBlocksAlways();

      window.addEventListener('beforeunload', this.handleBeforeUnload);
      if (this.imageTab) {
        this.selectedPromptId = 5; //image tab, id: 5
      }
      await this.loadPromptData();
    },
    beforeUnmount() {
      document.body.style.overflow = "";
      window.removeEventListener('beforeunload', this.handleBeforeUnload);
    },
    methods: {
      getReportScore(report, type = PDP_REPORT_CONTENT_TYPE_TITLE_COPY, promptValue = DEFAULT_PROMPT_VALUE) {
        const reportContent = this.findReportContent({
          report,
          type,
          promptValue,
        });

        const content = this.normalizeReportContent(reportContent?.content);

        if (content && typeof content === 'object' && content.score != null) {
          return content.score;
        }

        return report?.analysis_content?.score ?? 0;
      },
      normalizeReportContent(content) {
        if (!content) {
          return {};
        }

        if (typeof content === 'object') {
          return content;
        }

        if (typeof content !== 'string') {
          return {};
        }

        const trimmedContent = content.trim();

        if (!trimmedContent) {
          return {};
        }

        const looksLikeJson = (
          trimmedContent.startsWith('{') ||
          trimmedContent.startsWith('[')
        );

        if (!looksLikeJson) {
          return content;
        }

        try {
          return JSON.parse(trimmedContent);
        } catch (e) {
          return content;
        }
      },
      findReportContent({ report, type, promptValue }) {
        const contents = report?.report_contents;

        if (!Array.isArray(contents) || !contents.length) {
          return null;
        }

        const normalizedPromptValue = this.normalizePromptValue(promptValue);

        const sameTypeContents = contents.filter(item => {
          return Number(item.type) === Number(type);
        });

        if (!sameTypeContents.length) {
          return null;
        }

        const exactMatch = sameTypeContents.find(item => {
          return this.reportContentMatchesPrompt(item, normalizedPromptValue);
        });

        if (exactMatch) {
          return exactMatch;
        }

        const defaultMatch = sameTypeContents.find(item => {
          return this.reportContentMatchesPrompt(item, DEFAULT_PROMPT_VALUE);
        });

        if (defaultMatch) {
          return defaultMatch;
        }

        return sameTypeContents[0] || null;
      },

      reportContentMatchesPrompt(reportContent, promptValue) {
        const normalizedPromptValue = this.normalizePromptValue(promptValue);

        if (normalizedPromptValue === DEFAULT_PROMPT_VALUE) {
          return (
            !reportContent.custom_prompt_id ||
            reportContent.template === DEFAULT_PROMPT_VALUE
          );
        }

        return String(reportContent.custom_prompt_id) === String(normalizedPromptValue);
      },

      normalizePromptValue(value) {
        if (value === null || value === undefined || value === '') {
          return DEFAULT_PROMPT_VALUE;
        }

        return String(value);
      },
      getDefaultPromptOption() {
        return {
          value: 'default',
          label: 'Default Prompt',
          description: 'Default report prompt',
          prompt: null,
          type: null,
          isDefault: true,
        };
      },

      setPromptSettingsTab(tab) {
        this.promptSettingsTab = tab;
        this.promptSearchQuery = '';
      },

      async loadPromptData() {
        if (!this.$store || !this.$store.dispatch) {
          console.warn('Vuex store is not available');
          return;
        }

        await this.$store.dispatch('pdpPromptSettings/loadPrompts', {
          types: [
            CUSTOM_PROMPT_TYPE_ANALYTICS,
            CUSTOM_PROMPT_TYPE_IMAGE_REORDER,
            CUSTOM_PROMPT_TYPE_IMAGE_COMPARISON,
          ],
        });

        await this.$store.dispatch('pdpPromptSettings/loadSelectedPrompts');
      },

      async selectPromptForActiveTab(value) {
        const tab = this.promptSettingsTab === 'images'
          ? 'images'
          : 'titles';

        await this.selectPromptForTab(tab, value);
      },

      async selectPromptForTab(tab, value) {
        try {
          await this.$store.dispatch('pdpPromptSettings/updatePromptForTab', {
            tab,
            value,
          });

          this.$bvToast.toast('Prompt setting saved!', {
            title: 'Success',
            variant: 'success',
            solid: true,
          });
        } catch (error) {
          console.error('Failed to save prompt setting:', error);

          this.$bvToast.toast('Failed to save prompt setting', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          });
        }
      },
      isTabLocked(promptId) {
        return this.isShared && !this.isAuthenticated && this.lockedTabIds.includes(promptId);
      },
      isAmazonAsin(value) {
        if (!value || typeof value !== 'string') {
          return false;
        }

        return /^[A-Z0-9]{10}$/i.test(value.trim());
      },

      extractAmazonAsin(value) {
        if (!value || typeof value !== 'string') {
          return null;
        }

        const trimmed = value.trim();

        if (this.isAmazonAsin(trimmed)) {
          return trimmed.toUpperCase();
        }

        let parsedUrl = null;

        try {
          parsedUrl = new URL(trimmed);
        } catch (e) {
          return null;
        }

        const hostname = (parsedUrl.hostname || '').toLowerCase();

        if (!hostname.includes('amazon.')) {
          return null;
        }

        const patterns = [
          /\/dp\/([A-Z0-9]{10})(?:[/?]|$)/i,
          /\/gp\/product\/([A-Z0-9]{10})(?:[/?]|$)/i,
          /\/product\/([A-Z0-9]{10})(?:[/?]|$)/i
        ];

        for (const pattern of patterns) {
          const match = parsedUrl.pathname.match(pattern);
          if (match && match[1]) {
            return match[1].toUpperCase();
          }
        }

        return null;
      },

      buildAmazonUrlFromAsin(asin) {
        const country = this.selectedCountryConfig;

        return `https://${country.domain}/dp/${asin}`;
      },

      normalizeSourceEntries(rawValue) {
        if (!rawValue || typeof rawValue !== 'string') {
          return [];
        }

        return rawValue
          .split('\n')
          .map(item => item.trim())
          .filter(Boolean)
          .map(item => {
            const amazonAsin = this.extractAmazonAsin(item);

            if (amazonAsin) {
              return this.buildAmazonUrlFromAsin(amazonAsin);
            }

            return item;
          });
      },

      normalizeUrlsTextarea() {
        const normalizedEntries = this.normalizeSourceEntries(this.urls);
        this.urls = normalizedEntries.join('\n');

        return normalizedEntries;
      },
      severityBadgeClass(severity) {
        return {
          'badge-danger': severity === 'critical',
          'badge-warning': severity === 'warning',
          'badge-info': severity === 'info'
        };
      },
      handleBeforeUnload(e) {
        if (this.isDirty) {
          e.preventDefault();
          e.returnValue = '';
        }
      },

      autoSelectReport() {
        const urlParams = new URLSearchParams(window.location.search);
        const urlProductId = urlParams.get('product');
        const storageKey = `pdp_selected_report_${this.companyId}`;
        const savedReportId = localStorage.getItem(storageKey);

        // Try URL param first, then localStorage, then fall back to first item
        const candidateId = urlProductId || savedReportId;
        let findReport = null;

        if (candidateId) {
          findReport = this.reports.find(item => +item.id === +candidateId);
        }

        // Fall back to first available report
        if (!findReport && this.reportList.length) {
          findReport = this.reports.find(item => item.id === this.reportList[0].id);
        }

        if (findReport) {
          const pid = this.getId(findReport.url);
          this.selectedReport = {
            id: findReport.id,
            productId: pid,
            productName: findReport.product_name,
            name: pid + ' - ' + findReport.product_name,
            url: findReport.url,
            images: findReport.images,
            image_content: findReport.image_content,
            image_stat: findReport.image_stat,
            score: this.getReportScore(
              findReport,
              PDP_REPORT_CONTENT_TYPE_TITLE_COPY,
              this.selectedTitleCopyPromptValue
            ),
          };
        } else {
          this.isAddProductsExpanded = true;
        }
      },
      handleReportChange(val) {
        if (this.isDirty) {
          if (!window.confirm('You have unsaved changes! Are you sure you want to switch product?')) {
            return;
          }
        }
        this.selectedReport = val;
        this.isDirty = false;
      },
      toggleReportDropdown() {
        this.isReportDropdownOpen = !this.isReportDropdownOpen;
        if (this.isReportDropdownOpen) {
          this.reportSearchQuery = '';
          this.$nextTick(() => {
            this.$refs.reportSearchInput?.focus();
          });
        }
      },
      closeReportDropdown() {
        this.isReportDropdownOpen = false;
      },
      selectReport(option) {
        this.handleReportChange(option);
        this.isReportDropdownOpen = false;
        this.reportSearchQuery = '';
      },
      toggleReportHistory() {
        this.isReportHistoryOpen = !this.isReportHistoryOpen;
      },
      closeReportHistory() {
        this.isReportHistoryOpen = false;
      },
      getReportUserName(report) {
        if (report.user) {
          if (report.user.first_name || report.user.last_name) {
            return [report.user.first_name, report.user.last_name].filter(Boolean).join(' ');
          }
          return report.user.name || null;
        }
        return null;
      },
      formatHistoryDate(dateStr) {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      },
      formatRelativeTime(dateStr) {
        if (!dateStr) return '';
        const then = new Date(dateStr);
        const now = new Date();
        const diffMs = now - then;
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHr = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHr / 24);
        const diffMonth = Math.floor(diffDay / 30);
        const diffYear = Math.floor(diffDay / 365);

        if (diffSec < 60) return 'just now';
        if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
        if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
        if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
        if (diffMonth < 12) return `${diffMonth} month${diffMonth === 1 ? '' : 's'} ago`;
        return `${diffYear} year${diffYear === 1 ? '' : 's'} ago`;
      },
      setDateFilter(value) {
        this.historyDateFilter = value;
        this.isDateFilterOpen = false;
        if (value !== 'specific') {
          this.historySpecificDate = null;
          this.isDatePickerOpen = false;
        }
      },
      setUserFilter(name) {
        this.historyUserFilter = name;
        this.isUserFilterOpen = false;
      },
      closeDateDropdown() {
        this.isDateFilterOpen = false;
      },
      closeUserDropdown() {
        this.isUserFilterOpen = false;
      },
      closeDatePicker() {
        this.isDatePickerOpen = false;
      },
      closeHistoryDropdowns() {
        this.isDateFilterOpen = false;
        this.isDatePickerOpen = false;
        this.isUserFilterOpen = false;
      },
      datePickerPrevMonth() {
        const d = new Date(this.datePickerViewDate);
        d.setMonth(d.getMonth() - 1);
        this.datePickerViewDate = d;
      },
      datePickerNextMonth() {
        const d = new Date(this.datePickerViewDate);
        d.setMonth(d.getMonth() + 1);
        this.datePickerViewDate = d;
      },
      pickDate(day) {
        this.historySpecificDate = day.date;
        this.isDatePickerOpen = false;
      },
      setHistoryScope(scope) {
        this.historyScope = scope;
        this.historyDateFilter = 'any';
        this.historySpecificDate = null;
        this.isDatePickerOpen = false;
        this.isDateFilterOpen = false;
        this.historyUserFilter = null;
        this.isUserFilterOpen = false;
      },
      clearHistoryFilters() {
        this.historyDateFilter = 'any';
        this.historySpecificDate = null;
        this.isDatePickerOpen = false;
        this.historyUserFilter = null;
      },
      selectHistoryReport(report) {
        if (report.product_name === 'Processing...') return;
        const productId = this.getId(report.url);
        const selected = {
          id: report.id,
          productId,
          productName: report.product_name,
          name: productId + ' - ' + report.product_name,
          url: report.url,
          images: report.images,
          image_content: report.image_content,
          image_stat: report.image_stat,
          score: this.getReportScore(
            report,
            PDP_REPORT_CONTENT_TYPE_TITLE_COPY,
            this.selectedTitleCopyPromptValue
          )
        };
        this.handleReportChange(selected);
      },

      statusPillClass(status) {
        const s = (status || '').toLowerCase();
        return {
          'is-pass': s === 'pass',
          'is-warning': s === 'warning',
          'is-fail': s === 'fail'
        };
      },
      selectPrompt(prompt) {
        if (this.selectedPromptId === prompt.id) return;

        if (this.isDirty) {
          if (!window.confirm('You have unsaved changes! Are you sure you want to switch tabs?')) {
            return;
          }
        }
        this.isDirty = false;

        if (prompt.id === 2) {
          setTimeout(() => {
            this.decorateAfterBlocksAlways();
          }, 500);
        }
        this.selectedPromptId = prompt.id;
        this.isErrors = true;
      },
      toggleAnalysisSummary() {
        this.isAnalysisSummaryExpanded = !this.isAnalysisSummaryExpanded;
      },
      async updateReport(report) {
        const { data } = await axios.get(`/pdp-analysis/report/${report}`);

        if (!this.reports.find(item => item.id === data.id) && !this.addedReports.find(item => item.id === data.id)) {
          this.addedReports.push(data);
        }

        this.updatedReports = this.updatedReports.filter(item => item.id !== data.id)
        this.updatedReports.push(data);
        this.updatingReports.splice(this.updatingReports.indexOf(report), 1);

        if (this.selectedReport && this.selectedReport.url === data.url) {
          const latestInList = this.reportList.find(r => r.url === data.url);
          if (latestInList && latestInList.id !== this.selectedReport.id) {
            this.selectedReport = latestInList;
          }
        }

        this.$bvToast.toast(`Successfully regenerated report for ${this.getId(data.url)}!`, {
          title: 'Success',
          variant: 'success',
        })
      },
      async updateKeyword(keyword) {
        const { data } = await axios.get(`/pdp-analysis/keyword/${keyword}`);
        
        let report = this.updatedReports.find(item => item.id === data.pdp_report_id);
        if (!report) {
          report = this.reports.find(item => item.id === data.pdp_report_id);
        }
        
        if (!report) return;
        
        report = JSON.parse(JSON.stringify(report));
        
        report.keywords = report.keywords?.filter(item => item.id !== data.id) ?? [];
        report.keywords.unshift(data);
        
        this.updatedReports = this.updatedReports.filter(item => item.id !== report.id)
        this.updatedReports.push(report);
      },
      // async loadPdpPrompts() {
      //   const {data} = await axios.get('/banner/groups/index_custom_prompts', {
      //     params: {
      //       type: CUSTOM_PROMPT_TYPE_ANALYTICS,
      //       page: 1,
      //       paginate: false,
      //       is_pdp_list: 1
      //     }
      //   });
      //  
      //   this.prompts = [...data];
      // },
      selectSource(e, source) {
        e.preventDefault();
        this.selectedSource = source.id;
      },
      onFileAdded(file) {
        const defaultName = (file.name || '').replace(/\.(xlsx|xls|csv)$/i, '');
        const id = `${file.name}-${file.size}-${file.lastModified || Date.now()}`;
        if (this.uploadedFiles.some(f => f.id === id)) return;

        const item = {
          id,
          file,
          name: file.name,
          size: file.size,
          saveAsList: true,
          listName: defaultName,
          parsed: {},

          // Spreadsheet UI state
          sheetOptions: [],
          selectedSheetName: '',
          hasAutoFilter: false,
          selectAllSpreadsheetRows: false,
          parseMeta: null,
        };

        this.uploadedFiles.push(item);
        this.rebuildSpreadsheetData();
      },
      onFileRemoved(file) {
        if (!file) return;
        const id = `${file.name}-${file.size}-${file.lastModified || ''}`;
        this.uploadedFiles = this.uploadedFiles.filter(f => !f.id.startsWith(`${file.name}-${file.size}-`));
        this.rebuildSpreadsheetData();
      },
      removeUploadedFile(item) {
        if (this.$refs.fileInput && item.file) {
          try { this.$refs.fileInput.removeFile(item.file); } catch (e) { console.log(e); }
        }
        this.uploadedFiles = this.uploadedFiles.filter(f => f.id !== item.id);
        this.rebuildSpreadsheetData();
      },
      formatFileSize(bytes) {
        if (!bytes && bytes !== 0) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
      },
      rebuildSpreadsheetData() {
        this.sourceSpreadSheetArr = {};
        this.sourceError = false;
        this.isErrors = true;
        this.file = this.uploadedFiles.map(f => f.file);
        this.uploadedFiles.forEach(item => this.parseSpreadsheet(item));
      },
      parseSpreadsheet(item) {
        const fileA = item?.file;

        if (!fileA || !/\.(xlsx|xlsm|xls)$/i.test(fileA.name)) {
          return;
        }

        const reader = new FileReader();

        reader.onload = (e) => {
          const data = new Uint8Array(e.target.result);

          const workbook = XLSX.read(data, {
            type: 'array',
            cellStyles: true,
          });

          const visibleSheetNames = this.getVisibleSheetNames(workbook);

          item.sheetOptions = visibleSheetNames;

          if (!item.selectedSheetName || !visibleSheetNames.includes(item.selectedSheetName)) {
            item.selectedSheetName = visibleSheetNames[0] || workbook.SheetNames[0] || '';
          }

          const worksheet = workbook.Sheets[item.selectedSheetName];

          if (!worksheet || !worksheet['!ref']) {
            item.parsed = {};
            item.hasAutoFilter = false;
            item.parseMeta = null;

            this.isErrors = !this.hasAnyRequiredSpreadsheetColumn();
            this.sourceError = this.isErrors;
            return;
          }

          const autoFilterRef = worksheet['!autofilter']?.ref || null;

          item.hasAutoFilter = !!autoFilterRef;

          if (!item.hasAutoFilter) {
            item.selectAllSpreadsheetRows = false;
          }

          const parseRef = autoFilterRef || worksheet['!ref'];
          const range = XLSX.utils.decode_range(parseRef);

          const getCell = (row, col) => {
            const address = XLSX.utils.encode_cell({ r: row, c: col });
            return worksheet[address];
          };

          const getCellValue = (row, col) => {
            const cell = getCell(row, col);
            return cell?.v ?? '';
          };

          const isRowVisible = (rowIndex) => {
            return !worksheet['!rows']?.[rowIndex]?.hidden;
          };

          const shouldParseRow = (rowIndex) => {
            if (!item.hasAutoFilter) {
              return true;
            }

            if (item.selectAllSpreadsheetRows) {
              return true;
            }

            return isRowVisible(rowIndex);
          };

          const normalizeHeader = (header) => {
            return String(header || '')
              .replace(/\s+/g, ' ')
              .trim();
          };

          const isFilledValue = (value) => {
            return value !== undefined && value !== null && String(value).trim() !== '';
          };

          let rowFound = false;
          const perFileByRow = {};
          const globalByRow = {};

          const headerRowIndex = range.s.r;

          for (let col = range.s.c; col <= range.e.c; col++) {
            const header = normalizeHeader(getCellValue(headerRowIndex, col));

            if (!header) {
              continue;
            }

            if (this.isValidSpreadsheetHeader(header)) {
              rowFound = true;
            }

            if (!perFileByRow[header]) {
              perFileByRow[header] = {};
            }

            if (!globalByRow[header]) {
              globalByRow[header] = {};
            }

            for (let row = headerRowIndex + 1; row <= range.e.r; row++) {
              if (!shouldParseRow(row)) {
                continue;
              }

              const value = getCellValue(row, col);

              // Same header exists more than once:
              // keep the first non-empty value for this row.
              if (
                isFilledValue(value) &&
                !isFilledValue(perFileByRow[header][row])
              ) {
                perFileByRow[header][row] = value;
              }

              if (
                isFilledValue(value) &&
                !isFilledValue(globalByRow[header][row])
              ) {
                globalByRow[header][row] = value;
              }

              // Ensure row exists even if value is empty,
              // so column arrays stay aligned.
              if (!(row in perFileByRow[header])) {
                perFileByRow[header][row] = '';
              }

              if (!(row in globalByRow[header])) {
                globalByRow[header][row] = '';
              }
            }
          }

          const perFile = {};
          Object.keys(perFileByRow).forEach((header) => {
            perFile[header] = Object.keys(perFileByRow[header])
              .sort((a, b) => Number(a) - Number(b))
              .map((row) => perFileByRow[header][row]);
          });

          Object.keys(globalByRow).forEach((header) => {
            if (!this.sourceSpreadSheetArr[header]) {
              this.sourceSpreadSheetArr[header] = [];
            }

            const values = Object.keys(globalByRow[header])
              .sort((a, b) => Number(a) - Number(b))
              .map((row) => globalByRow[header][row]);

            this.sourceSpreadSheetArr[header].push(...values);
          });

          item.parsed = perFile;
          item.parseMeta = {
            sheetName: item.selectedSheetName,
            autoFilterRef,
            selectAllRows: item.selectAllSpreadsheetRows,
          };

          this.isErrors = !this.hasAnyRequiredSpreadsheetColumn();
          this.sourceError = this.isErrors;

          if (!rowFound && !this.hasAnyRequiredSpreadsheetColumn()) {
            this.sourceError = true;
          }
        };

        reader.readAsArrayBuffer(fileA);
      },
      handleSpreadsheetSheetChange(item) {
        if (!item) return;

        item.parsed = {};
        item.hasAutoFilter = false;
        item.selectAllSpreadsheetRows = false;
        item.parseMeta = null;

        this.rebuildSpreadsheetData();
      },

      handleSpreadsheetSelectAllChange(item) {
        if (!item) return;

        item.parsed = {};
        item.parseMeta = null;

        this.rebuildSpreadsheetData();
      },

      getVisibleSheetNames(workbook) {
        return workbook.SheetNames.filter((name, index) => {
          const meta = workbook.Workbook?.Sheets?.[index];
          return !meta || meta.Hidden === 0 || meta.Hidden === undefined;
        });
      },

      isValidSpreadsheetHeader(header) {
        const value = String(header || '');

        return [
          'ASIN',
          'Product ID',
          'GTIN',
          'ASIN/Product ID',
          'EAN/UPC',
          'Speck SKU Code',
        ].some((needle) => value.includes(needle));
      },

      hasAnyRequiredSpreadsheetColumn() {
        return Object.keys(this.sourceSpreadSheetArr || {}).some((header) => {
          return this.isValidSpreadsheetHeader(header);
        });
      },
      async prepareReport() {
        let req = {}
        req.source = this.selectedSource;
        
        if (this.selectedSource === 'asin_urls') {
          let urls = this.urls || '';

          if (this.selectedProducts && this.selectedProducts.length > 0) {
            const productUrls = this.selectedProducts.map(product => {
              if (product.asin) return `https://www.amazon.com/dp/${product.asin}`;
              if (product.tcin) return `https://www.target.com/p/-/A-${product.tcin}`;
              if (product.walmart_id) return `https://www.walmart.com/ip/${product.walmart_id}`;
              if (product.url) return product.url;
              return '';
            }).filter(url => url.trim());

            if (productUrls.length > 0) {
              urls = urls ? urls + '\n' + productUrls.join('\n') : productUrls.join('\n');
            }
          }

          let normalizedEntries = this.normalizeUrlsTextarea();
          
          urls = normalizedEntries.join('\n');

          req.urls = urls;
          req.country_params = this.selectedCountryConfig;
        }
        
        if (this.selectedSource === 'spreadsheet') {
          req.spreadsheet = this.sourceSpreadSheetArr;
          req.files_meta = this.uploadedFiles
            .filter(f => f.saveAsList && f.listName && f.listName.trim())
            .map(f => ({
              file_name: f.name,
              list_name: f.listName.trim(),
              save_as_list: true,
              spreadsheet: f.parsed || {},
            }));
        }
        
        if (this.excludedKeywords) {
          req.excluded_keywords = this.excludedKeywords;
        }
        
        if (this.keywords) {
          req.keywords = this.keywords;
        }
        this.isProcessing = true;
        const {data} = await axios.post('/pdp-analysis/prepare-report', req);
        this.isProcessing = false;
        this.hideSource = true;
        
        let reportId = null;
        
        if (data?.reports?.length) {
          reportId = data.reports[0].id;
        }

        let url = location.origin + location.pathname;

        const params = new URLSearchParams(location.search);

        if (reportId) {
          params.set('report', reportId);
        }

        if ([...params].length > 0) {
          url += '?' + params.toString();
        }

        url += '#pdp-analysis';
        
        location.replace(url);
        location.reload();
      },
      getId(url) {
        if (!url || typeof url !== 'string') return '0';
        url = url.trim();

        const asinMatch = url.match(/(?:dp|gp\/product)\/([A-Z0-9]{10})/i)
          || url.match(/\/([A-Z0-9]{10})(?:[/?]|$)/i)
          || url.match(/^([A-Z0-9]{10})$/i);
        if (asinMatch) {
          return asinMatch[1].toUpperCase();
        }

        const walmartMatch = url.match(/walmart\.com\/ip\/(\d+)/i)
          || url.match(/\/(\d+)(?:[/?]|$)/)
          || url.match(/^(\d{6,})$/);
        if (walmartMatch) {
          return walmartMatch[1];
        }

        return url;
      },
      async copyAction(type = 'id') {
        switch (type) {
          case 'id':
            await navigator?.clipboard?.writeText(this.getId(this.reportData.url));
            break;
          case 'url':
            await navigator?.clipboard?.writeText(this.reportData.url);
            break;
        }
        this.$bvToast.toast('Copied to clipboard!', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      },
      viewProduct(selectedReport) {
        if (!selectedReport?.url) {
          this.$bvToast.toast('Product URL not available', { title: 'Warning', variant: 'warning' });
          return;
        }
        try {
          const url = new URL(selectedReport.url);
          if (url.protocol === 'http:' || url.protocol === 'https:') {
            window.open(selectedReport.url, '_blank');
          }
        } catch {
          this.$bvToast.toast('Invalid product URL', { title: 'Error', variant: 'danger' });
        }
      },
      async shareReport() {
        if (!this.selectedReport?.id) return;
        try {
          const { data } = await axios.post('/pdp-analysis/generate-share-link/' + this.selectedReport.id);
          if (data?.share_url) {
            window.open(data.share_url, '_blank');
          }
        } catch (e) {
          this.$bvToast.toast('Failed to generate share link', { title: 'Error', variant: 'danger', solid: true });
        }
      },
      async deleteReport() {
        const allow = await this.$bvModal.msgBoxConfirm('Are you sure you want to delete this report?');
        
        if (!allow) return;
        
        await axios.delete('/pdp-analysis/delete-report/' + this.selectedReport.id)
        this.deletedReports.push(this.selectedReport.id);
        this.selectedReport = null;
        this.$bvToast.toast('Successfully deleted report!', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      },
      async regenerateReport() {
        const { keywords, excludeKeywords } = this.readOptimizeKeywordsFromDOM();

        this.updatingReports.push(this.selectedReport.id);

        const payload = {};
        if (keywords && keywords.length) {
          payload.keywords = keywords.join('\n');
        }
        if (excludeKeywords && excludeKeywords.length) {
          payload.exclude_keywords = excludeKeywords.join('\n');
        }

        const { data } = await axios.post('/pdp-analysis/regenerate-report/' + this.selectedReport.id, payload)

        // Add a processing placeholder to the history list
        if (data.report_id) {
          this.addedReports.push({
            id: data.report_id,
            url: this.selectedReport.url,
            product_name: 'Processing...',
            analysis_content: {},
            user_id: this.userId,
            user: null,
            created_at: new Date().toISOString(),
          });
        }

        this.$bvToast.toast('Report generation started, please check back later!', {
          title: 'Info',
          variant: 'info',
        })
      },
      async regenerateReportImages() {
        
        this.updatingReports.push(this.selectedReport.id);
        
        await axios.post('/pdp-analysis/regenerate-report-images/' + this.selectedReport.id)
        
        this.$bvToast.toast('Report images generation started, please check back later!', {
          title: 'Info',
          variant: 'info',
        })
      },
      async addKeyword() {
        let data = {}
        data.keyword = this.newKeyword;
        
        await axios.post('/pdp-analysis/add-keyword/' + this.selectedReport.id, data)
        
        this.newKeyword = '';
        this.showKeywordForm = false;
        this.$bvToast.toast('Keyword added!', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      },
      async reCrawlKeywords() {
        await axios.get('/pdp-analysis/re-crawl-keywords/' + this.selectedReport.id)
        
        this.$bvToast.toast('Re-crawl started, please check back later!', {
          title: 'Info',
          variant: 'info',
        })
      },
      async deleteKeyword(keyword) {
        let accept = await this.$bvModal.msgBoxConfirm('Are you sure you want to delete this keyword?')
        
        if (!accept) return;
        
        await axios.delete('/pdp-analysis/delete-keyword/' + keyword.id)
        let report = this.updatedReports.find(item => item.id === this.reportData.id);
        if (!report) {
          report = this.reports.find(item => item.id === this.reportData.id);
        }
        
        if (!report) return;
        report = JSON.parse(JSON.stringify(report));
        report.keywords = report.keywords.filter(item => item.id !== keyword.id);
        this.updatedReports = this.updatedReports.filter(item => item.id !== report.id)
        this.updatedReports.push(report);
        
        this.$bvToast.toast('Keyword deleted!', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      },
      changeSource() {
        this.selectedSource = null;
        this.urls = '';
        this.sourceSpreadSheetArr = [];
        this.isErrors = true;
        this.selectedProductLists = [];
        this.selectedProducts = [];
        this.availableProducts = [];
        this.productTypeFilter = 'all';
      },
      updateUrlWithProduct(report) {
        const url = new URL(window.location.href);
        const storageKey = `pdp_selected_report_${this.companyId}`;

        if (report && report.id) {
          url.searchParams.set('product', report.id);
          this.isAddProductsExpanded = false;
          localStorage.setItem(storageKey, report.id);
        } else {
          url.searchParams.delete('product');
          this.isAddProductsExpanded = true;
          localStorage.removeItem(storageKey);
        }

        window.history.replaceState({}, '', url.toString());
      },
      toggleAddProducts() {
        if (this.isDirty && !this.isAddProductsExpanded) {
          if (!window.confirm('You have unsaved changes! Are you sure you want to proceed?')) {
            return;
          }
        }
        this.isAddProductsExpanded = !this.isAddProductsExpanded;
        if(this.isAddProductsExpanded){
          this.selectedReport = null;
          this.isDirty = false;
        }
      },
      async loadAvailableProductLists() {
        this.isLoadingProductLists = true;
        try {
          const response = await axios.get('/product-id-list/all');
          this.availableProductLists = response.data.productIdLists || [];

          if (this.availableProductLists.length === 1) {
            this.selectedProductLists = [this.availableProductLists[0]];
            this.loadProductsFromLists();
          }
        } catch (error) {
          console.error('Failed to load product lists:', error);
        } finally {
          this.isLoadingProductLists = false;
        }
      },
      async loadProductsFromLists() {
        if (!this.selectedProductLists || this.selectedProductLists.length === 0) {
          this.availableProducts = [];
          this.selectedProducts = [];
          this.productTypeFilter = 'all';
          return;
        }
        this.productTypeFilter = 'all';

        this.isLoadingProducts = true;
        try {
          const allProducts = [];
          for (const productList of this.selectedProductLists) {
            const productListId = productList.id || productList;
            const response = await axios.get(`/product-id-list/${productListId}/data`);
            const records = response.data.records || [];

            records.forEach(record => {
              const productId = record.asin || record.tcin || record.walmart_id || record.salsify_id || record.url;
              if (productId) {
                const displayName = this.getProductDisplayName(record);
                allProducts.push({
                  ...record,
                  id: record.id,
                  record_id: record.id,
                  product_id: productId,
                  display_name: displayName,
                  product_list_name: productList.name,
                  asin: record.asin,
                  tcin: record.tcin,
                  walmart_id: record.walmart_id,
                  salsify_id: record.salsify_id,
                  url: record.url
                });
              }
            });
          }

          this.availableProducts = allProducts;
        } catch (error) {
          console.error('Failed to load products from lists:', error);
        } finally {
          this.isLoadingProducts = false;
        }
      },
      getProductDisplayName(record) {
        const productId = record.asin || record.tcin || record.walmart_id || record.salsify_id || record.url;
        let type = '';

        if (record.asin) type = 'ASIN';
        else if (record.tcin) type = 'TCIN';
        else if (record.walmart_id) type = 'Walmart ID';
        else if (record.salsify_id) type = 'Salsify ID';
        else if (record.url) type = 'URL';

        return `${productId} (${type})`;
      },
      selectAllProducts() {
        if (this.selectedProducts.length === this.filteredProducts.length) {
          return this.clearAllProducts();
        }
        this.selectedProducts = [...this.filteredProducts];
        this.populateSelectedProducts();
      },
      clearAllProducts() {
        this.selectedProducts = [];
      },
      handleTypeFilterChange() {
        if (this.productTypeFilter === 'all') {
          this.selectedProducts = [...this.availableProducts];
        } else {
          this.selectedProducts = [...this.filteredProducts];
        }
        this.updateIsErrors();
      },
      limitText(count) {
        return `and ${count} more`;
      },
      populateSelectedProducts() {
        this.updateIsErrors();
      },
      updateIsErrors() {
        if (this.urls || (this.selectedProducts && this.selectedProducts.length > 0)) {
          this.isErrors = false;
        } else {
          this.isErrors = true;
        }
      },
      openLightbox(src) {
        this.lightbox.src = src;
        this.lightbox.isOpen = true;

        document.body.style.overflow = "hidden";
      },
      closeLightbox() {
        this.lightbox.isOpen = false;
        this.lightbox.src = null;
        document.body.style.overflow = "";
      },
      async xlsxExport(id = 'all') {
        this.$bvToast.toast('Preparing XLSX, please check back later!', {
          title: 'Info',
          variant: 'info',
        })
        try {
          let url = '/pdp-analysis/export-xlsx';
          if (id !== 'all') {
            url += '?report_id=' + encodeURIComponent(id);
          } else {
            url += '?ids=' + this.reports.map(report => report.id).join(',')
          }

          const response = await axios.get(url, {
            responseType: 'blob',
            headers: {
              'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            },
          });

          // Try to get filename from Content-Disposition
          let filename = 'pdp_reports.xlsx';
          const cd = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
          if (cd) {
            // supports: filename="xxx.xlsx"  OR  filename*=UTF-8''xxx.xlsx
            const m1 = cd.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
            const m2 = cd.match(/filename="?([^"]+)"?/i);
            const raw = (m1 && m1[1]) || (m2 && m2[1]);
            if (raw) filename = decodeURIComponent(raw.trim().replace(/(^"|"$)/g, ''));
          } else if (id !== 'all') {
            filename = `pdp_report_${id}.xlsx`;
          }

          const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });

          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(link.href);
        } catch (e) {
          console.error('xlsxExport error', e);
        }
      },
      async xlsxOptimizationAfterExport(id = 'all') {
        let url = '/pdp-analysis/export-optimization-after-xlsx';
        if (id !== 'all') {
          url += '?report_id=' + encodeURIComponent(id);
        } else {
          url += '?ids=' + this.reports.map(report => report.id).join(',')
        }


        const response = await axios.get(url, { responseType: 'blob' });

        let filename = id === 'all'
          ? 'pdp_optimization.xlsx'
          : `pdp_optimization_${id}.xlsx`;

        const cd = response.headers?.['content-disposition'];
        if (cd) {
          const m1 = cd.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
          const m2 = cd.match(/filename="?([^"]+)"?/i);
          const raw = (m1 && m1[1]) || (m2 && m2[1]);
          if (raw) filename = decodeURIComponent(raw.trim().replace(/(^"|"$)/g, ''));
        }

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(link.href);
      },
      publishReport() {
        this.$bvModal.show('optix-publish-modal');
      },
      onRootInput() {
        this.isDirty = true;
      },
      onRootClick(e) {
        const kwRemoveBtn = e.target.closest?.('[data-kw-remove]');
        if (kwRemoveBtn) {
          e.stopPropagation();
          const kw = kwRemoveBtn.getAttribute('data-kw-remove');
          this.editingKeywords = this.editingKeywords.filter(k => k !== kw);
          if (this.keywordBeforeBoxEl) {
            this.renderKeywordsEditUI(this.keywordBeforeBoxEl);
          }
          return;
        }

        const kwPencilBtn = e.target.closest?.('[data-opt-kw-pencil="1"]');
        if (kwPencilBtn) {
          e.stopPropagation();
          const box = kwPencilBtn.closest?.('.opt-before-kw-box');
          if (box) {
            this.startKeywordsEdit(box);
          }
          return;
        }

        const excRemoveBtn = e.target.closest?.('[data-exc-remove]');
        if (excRemoveBtn) {
          e.stopPropagation();
          const kw = excRemoveBtn.getAttribute('data-exc-remove');
          this.editingExcludedKeywords = this.editingExcludedKeywords.filter(k => k !== kw);
          if (this.excludedKeywordBeforeBoxEl) {
            this.renderExcludedKeywordsEditUI(this.excludedKeywordBeforeBoxEl);
          }
          return;
        }

        const excPencilBtn = e.target.closest?.('[data-opt-exc-pencil="1"]');
        if (excPencilBtn) {
          e.stopPropagation();
          const box = excPencilBtn.closest?.('.opt-before-exc-box');
          if (box) {
            this.startExcludedKeywordsEdit(box);
          }
          return;
        }

        const pencilBtn = e.target.closest?.('[data-opt-pencil="1"]');
        if (!pencilBtn) return;

        const box = pencilBtn.closest?.('.opt-editable-box');
        if (!box) return;

        if (!this.isEditing) {
          this.startEditMode();
        }

        this.$nextTick(() => {
          // IMPORTANT: box reference might be stale if DOM re-rendered
          // Try to re-find the closest editable box again
          const freshBox = pencilBtn.closest?.('.opt-editable-box') || box;
          this.enableBox(freshBox);
        });
      },

      // ----------------------------------------
      // Edit mode lifecycle
      // ----------------------------------------
      startEditMode() {
        this.isEditing = true;
        this.originalHtml = this.reportData.optimize_content || '';
        this.$nextTick(() => this.decorateAfterBlocksAlways());
      },

      stopEditMode() {
        this.isEditing = false;
        this.disableActiveBox();
        this.isDirty = false;
        this.isEditingKeywords = false;
        this.editingKeywords = [];
        this.keywordBeforeBoxEl = null;
        this.originalBeforeKeywordsHtml = '';
        this.isEditingExcludedKeywords = false;
        this.editingExcludedKeywords = [];
        this.excludedKeywordBeforeBoxEl = null;
        this.originalBeforeExcludedKeywordsHtml = '';
        // do NOT remove pencils (they should always show)
      },

      cancelEdit() {
        this.disableActiveBox();
        this.activeBoxEl = null;

        if (this.isEditingKeywords && this.keywordBeforeBoxEl && this.originalBeforeKeywordsHtml) {
          this.keywordBeforeBoxEl.innerHTML = this.originalBeforeKeywordsHtml;
        }
        if (this.isEditingExcludedKeywords && this.excludedKeywordBeforeBoxEl && this.originalBeforeExcludedKeywordsHtml) {
          this.excludedKeywordBeforeBoxEl.innerHTML = this.originalBeforeExcludedKeywordsHtml;
        }

        if (this.$refs.optRoot) {
          this.$refs.optRoot.innerHTML = this.originalHtml;
        }

        // Keep pencils visible after revert
        this.$nextTick(() => {
          this.decorateAfterBlocksAlways();
        });

        this.stopEditMode();
      },

      // ----------------------------------------
      // Enable/disable editing for one "After" box
      // ----------------------------------------
      enableBox(boxEl) {
        if (!this.isEditing || !boxEl) return;

        this.disableActiveBox();

        this.activeBoxEl = boxEl;

        boxEl.classList.add('opt-active');
        boxEl.classList.add('opt-editable-box');

        // Make box editable, but lock label div(s)
        boxEl.setAttribute('contenteditable', 'true');

        const labels = boxEl.querySelectorAll('div.small');
        labels.forEach((l) => l.setAttribute('contenteditable', 'false'));

        // Focus for typing
        try {
          boxEl.focus();
          const selection = window.getSelection();
          const range = document.createRange();

          const firstEditableNode = Array.from(boxEl.childNodes).find((node) => {
            if (node.nodeType === Node.TEXT_NODE) {
              return node.textContent.trim().length > 0;
            }

            if (node.nodeType === Node.ELEMENT_NODE) {
              const el = node;
              return !el.classList?.contains('small') && el.textContent.trim().length > 0;
            }

            return false;
          });

          if (firstEditableNode) {
            range.setStart(firstEditableNode, 0);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        } catch (err) {
          // ignore
        }
      },

      disableActiveBox() {
        if (!this.activeBoxEl) return;

        this.activeBoxEl.removeAttribute('contenteditable');
        this.activeBoxEl.classList.remove('opt-active');

        const labels = this.activeBoxEl.querySelectorAll('div.small');
        labels.forEach((l) => l.removeAttribute('contenteditable'));

        this.activeBoxEl = null;
      },

      // ----------------------------------------
      // Always show pencils ONLY for needed modules
      // ----------------------------------------
      decorateAfterBlocksAlways() {
        const root = this.$refs.optRoot;
        if (!root) return;

        this.decorateBeforeKeywordsBox();
        this.decorateBeforeExcludedKeywordsBox();

        const allowedModules = new Set([
          'Product Title',
          'Bullet Points',
          'Product Description',
        ]);

        const afterLabels = root.querySelectorAll('div.small.text-success');

        afterLabels.forEach((label) => {
          const labelText = (label.textContent || '').trim();
          if (!labelText.includes('After')) return;

          const moduleTitle = this.getModuleTitleForNode(label);
          if (!moduleTitle || !allowedModules.has(moduleTitle)) return;

          const box = label.parentElement; // bordered box
          if (!box) return;

          box.classList.add('opt-editable-box');
          box.setAttribute('data-opt-module', moduleTitle);

          // Add pencil once
          if (!box.querySelector('[data-opt-pencil="1"]')) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.setAttribute('data-opt-pencil', '1');
            btn.className = 'opt-pencil btn btn-light btn-sm';
            btn.innerHTML = '<i class="bi bi-pencil" />';
            box.appendChild(btn);
          }
        });
      },

      getModuleTitleForNode(node) {
        let el = node;
        for (let i = 0; i < 30 && el; i++) {
          if (el.classList && el.classList.contains('col-12')) {
            const h5 = el.querySelector('h5');
            if (h5) return (h5.textContent || '').trim();
          }
          el = el.parentElement;
        }
        return '';
      },

      // ----------------------------------------
      // Save to backend (update optimize_content)
      // ----------------------------------------
      async saveOptimized() {
        if (!this.$refs.optRoot) return;

        this.isSaving = true;
        try {
          this.disableActiveBox();

          if (this.isEditingKeywords && this.keywordBeforeBoxEl) {
            this.finalizeKeywordsEdit();
          }
          if (this.isEditingExcludedKeywords && this.excludedKeywordBeforeBoxEl) {
            this.finalizeExcludedKeywordsEdit();
          }

          const newHtml = this.$refs.optRoot.innerHTML;

          // CHANGE THIS to your real route if different:
          // Route::post('/save-optimize-content/{report}', ...)
          await axios.post(`/pdp-analysis/save-optimize-content/${this.reportData.id}`, {
            optimize_content: newHtml,
          });

          // Update local state
          this.reportData.optimize_content = newHtml;
          this.originalHtml = newHtml;

          this.stopEditMode();

          await this.$nextTick();
          await this.$nextTick();
          this.decorateAfterBlocksAlways();
        } catch (e) {
          console.error('saveOptimized error', e);
        } finally {
          this.isSaving = false;
        }
      },
      setSortMode(mode) {
        this.sortMode = mode;
      },

      escapeHtml(str) {
        return String(str)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      },

      decorateBeforeKeywordsBox() {
        const root = this.$refs.optRoot;
        if (!root) return;

        const h5s = root.querySelectorAll('h5');
        let kwModuleEl = null;
        h5s.forEach(h5 => {
          if ((h5.textContent || '').trim() === 'Keywords') {
            let el = h5;
            for (let i = 0; i < 10 && el; i++) {
              if (el.classList && el.classList.contains('col-12')) {
                kwModuleEl = el;
                break;
              }
              el = el.parentElement;
            }
            if (!kwModuleEl) kwModuleEl = h5.parentElement;
          }
        });

        if (!kwModuleEl) return;

        const labels = kwModuleEl.querySelectorAll('div.small');
        labels.forEach(label => {
          const labelText = (label.textContent || '').trim();
          if (!labelText.includes('Before')) return;

          const box = label.parentElement;
          if (!box) return;

          box.classList.add('opt-before-kw-box');

          if (!box.querySelector('[data-opt-kw-pencil="1"]')) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.setAttribute('data-opt-kw-pencil', '1');
            btn.className = 'opt-pencil btn btn-light btn-sm';
            btn.innerHTML = '<i class="bi bi-pencil"></i>';
            box.appendChild(btn);
          }
        });
      },

      startKeywordsEdit(boxEl) {
        if (this.isEditingKeywords) return;

        this.keywordBeforeBoxEl = boxEl;
        this.originalBeforeKeywordsHtml = boxEl.innerHTML;

        const keywords = [];
        boxEl.childNodes.forEach(node => {
          if (node.nodeType === Node.TEXT_NODE) {
            node.textContent.split(/[\n,]+/).forEach(part => {
              const kw = part.trim();
              if (kw) keywords.push(kw);
            });
            return;
          }
          if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node;
            if (el.classList.contains('small')) return;
            if (el.hasAttribute('data-opt-kw-pencil')) return;
            if (el.classList.contains('kw-before-pills-wrap')) {
              el.querySelectorAll('.kw-before-pill').forEach(pill => {
                const text = (pill.textContent || '').trim();
                if (text) keywords.push(text);
              });
              return;
            }
            const text = (el.textContent || '').trim();
            if (text) keywords.push(text);
          }
        });

        this.editingKeywords = keywords;
        this.isEditingKeywords = true;

        if (!this.isEditing) {
          this.startEditMode();
        }

        this.renderKeywordsEditUI(boxEl);
      },

      renderKeywordsEditUI(boxEl) {
        const labelEl = boxEl.querySelector('div.small');
        const labelHtml = labelEl
          ? labelEl.outerHTML
          : '<div class="small text-muted">Before</div>';

        const pillsHtml = this.editingKeywords
          .map(kw =>
            `<span class="kw-edit-pill">${this.escapeHtml(kw)}<button type="button" class="kw-pill-remove" data-kw-remove="${this.escapeHtml(kw)}" title="Remove">×</button></span>`
          )
          .join('');

        boxEl.innerHTML = `
          ${labelHtml}
          <div class="kw-edit-wrap">
            <div class="kw-edit-pills">${pillsHtml}</div>
            <input
              type="text"
              class="kw-edit-input"
              placeholder="Type keyword, press Enter to add…"
              data-kw-input="1"
            />
          </div>
          <button type="button" class="opt-pencil btn btn-light btn-sm" data-opt-kw-pencil="1"><i class="bi bi-pencil"></i></button>
        `;

        const input = boxEl.querySelector('[data-kw-input="1"]');
        if (input) {
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              const val = input.value.trim().replace(/,+$/, '');
              if (val && !this.editingKeywords.includes(val)) {
                this.editingKeywords.push(val);
                this.renderKeywordsEditUI(this.keywordBeforeBoxEl);
              } else {
                input.value = '';
              }
            }
          });
          input.focus();
        }
      },

      finalizeKeywordsEdit() {
        if (!this.keywordBeforeBoxEl) return;

        const input = this.keywordBeforeBoxEl.querySelector('[data-kw-input="1"]');
        if (input) {
          const val = input.value.trim().replace(/,+$/, '');
          if (val && !this.editingKeywords.includes(val)) {
            this.editingKeywords.push(val);
          }
        }

        const labelEl = this.keywordBeforeBoxEl.querySelector('div.small');
        const labelHtml = labelEl
          ? labelEl.outerHTML
          : '<div class="small text-muted">Before</div>';

        const pillsHtml = this.editingKeywords
          .map(kw => `<span class="kw-before-pill">${this.escapeHtml(kw)}</span>`)
          .join('\n');

        this.keywordBeforeBoxEl.innerHTML = `${labelHtml}\n<div class="kw-before-pills-wrap">${pillsHtml}</div>`;
        this.keywordBeforeBoxEl.classList.remove('opt-active');
      },

      decorateBeforeExcludedKeywordsBox() {
        const root = this.$refs.optRoot;
        if (!root) return;

        const h5s = root.querySelectorAll('h5');
        let moduleEl = null;
        h5s.forEach(h5 => {
          if ((h5.textContent || '').trim().toLowerCase().includes('excluded')) {
            let el = h5;
            for (let i = 0; i < 10 && el; i++) {
              if (el.classList && el.classList.contains('col-12')) {
                moduleEl = el;
                break;
              }
              el = el.parentElement;
            }
            if (!moduleEl) moduleEl = h5.parentElement;
          }
        });

        if (!moduleEl) return;

        const labels = moduleEl.querySelectorAll('div.small');
        labels.forEach(label => {
          const labelText = (label.textContent || '').trim();
          if (!labelText.includes('Before')) return;

          const box = label.parentElement;
          if (!box) return;

          box.classList.add('opt-before-exc-box');

          if (!box.querySelector('[data-opt-exc-pencil="1"]')) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.setAttribute('data-opt-exc-pencil', '1');
            btn.className = 'opt-pencil btn btn-light btn-sm';
            btn.innerHTML = '<i class="bi bi-pencil"></i>';
            box.appendChild(btn);
          }
        });
      },

      startExcludedKeywordsEdit(boxEl) {
        if (this.isEditingExcludedKeywords) return;

        this.excludedKeywordBeforeBoxEl = boxEl;
        this.originalBeforeExcludedKeywordsHtml = boxEl.innerHTML;

        const keywords = [];
        boxEl.childNodes.forEach(node => {
          if (node.nodeType === Node.TEXT_NODE) {
            node.textContent.split(/[\n,]+/).forEach(part => {
              const kw = part.trim();
              if (kw) keywords.push(kw);
            });
            return;
          }
          if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node;
            if (el.classList.contains('small')) return;
            if (el.hasAttribute('data-opt-exc-pencil')) return;
            if (el.classList.contains('exc-before-pills-wrap')) {
              el.querySelectorAll('.exc-before-pill').forEach(pill => {
                const text = (pill.textContent || '').trim();
                if (text) keywords.push(text);
              });
              return;
            }
            const text = (el.textContent || '').trim();
            if (text) keywords.push(text);
          }
        });

        this.editingExcludedKeywords = keywords;
        this.isEditingExcludedKeywords = true;

        if (!this.isEditing) {
          this.startEditMode();
        }

        this.renderExcludedKeywordsEditUI(boxEl);
      },

      renderExcludedKeywordsEditUI(boxEl) {
        const labelEl = boxEl.querySelector('div.small');
        const labelHtml = labelEl
          ? labelEl.outerHTML
          : '<div class="small text-muted">Before</div>';

        const pillsHtml = this.editingExcludedKeywords
          .map(kw =>
            `<span class="kw-edit-pill">${this.escapeHtml(kw)}<button type="button" class="kw-pill-remove" data-exc-remove="${this.escapeHtml(kw)}" title="Remove">×</button></span>`
          )
          .join('');

        boxEl.innerHTML = `
          ${labelHtml}
          <div class="kw-edit-wrap">
            <div class="kw-edit-pills">${pillsHtml}</div>
            <input
              type="text"
              class="kw-edit-input"
              placeholder="Type excluded keyword, press Enter to add…"
              data-exc-input="1"
            />
          </div>
          <button type="button" class="opt-pencil btn btn-light btn-sm" data-opt-exc-pencil="1"><i class="bi bi-pencil"></i></button>
        `;

        const input = boxEl.querySelector('[data-exc-input="1"]');
        if (input) {
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              const val = input.value.trim().replace(/,+$/, '');
              if (val && !this.editingExcludedKeywords.includes(val)) {
                this.editingExcludedKeywords.push(val);
                this.renderExcludedKeywordsEditUI(this.excludedKeywordBeforeBoxEl);
              } else {
                input.value = '';
              }
            }
          });
          input.focus();
        }
      },

      finalizeExcludedKeywordsEdit() {
        if (!this.excludedKeywordBeforeBoxEl) return;

        const input = this.excludedKeywordBeforeBoxEl.querySelector('[data-exc-input="1"]');
        if (input) {
          const val = input.value.trim().replace(/,+$/, '');
          if (val && !this.editingExcludedKeywords.includes(val)) {
            this.editingExcludedKeywords.push(val);
          }
        }

        const labelEl = this.excludedKeywordBeforeBoxEl.querySelector('div.small');
        const labelHtml = labelEl
          ? labelEl.outerHTML
          : '<div class="small text-muted">Before</div>';

        const pillsHtml = this.editingExcludedKeywords
          .map(kw => `<span class="exc-before-pill">${this.escapeHtml(kw)}</span>`)
          .join('\n');

        this.excludedKeywordBeforeBoxEl.innerHTML = `${labelHtml}\n<div class="exc-before-pills-wrap">${pillsHtml}</div>`;
        this.excludedKeywordBeforeBoxEl.classList.remove('opt-active');
      },

      readOptimizeKeywordsFromDOM() {
        if (!this.$refs.optRoot) return { keywords: null, excludeKeywords: null };

        const extractEditPills = (removeAttr) => {
          const kws = [];
          this.$refs.optRoot.querySelectorAll(`[${removeAttr}]`).forEach(btn => {
            const text = (btn.getAttribute(removeAttr) || '').trim();
            if (text) kws.push(text);
          });
          return kws.length ? kws : null;
        };

        const extractStaticPills = (pillClass) => {
          const kws = [];
          this.$refs.optRoot.querySelectorAll('.' + pillClass).forEach(pill => {
            const text = (pill.textContent || '').trim();
            if (text) kws.push(text);
          });
          return kws.length ? kws : null;
        };

        const editKw = extractEditPills('data-kw-remove');
        const staticKw = extractStaticPills('kw-before-pill');
        const editExc = extractEditPills('data-exc-remove');
        const staticExc = extractStaticPills('exc-before-pill');

        let keywords = editKw ? [...editKw] : (staticKw ? [...staticKw] : null);

        if (keywords) {
          const kwInput = this.$refs.optRoot.querySelector('[data-kw-input="1"]');
          if (kwInput) {
            const val = kwInput.value.trim().replace(/,+$/, '');
            if (val && !keywords.includes(val)) {
              keywords.push(val);
            }
          }
        }

        let excludeKeywords = editExc ? [...editExc] : (staticExc ? [...staticExc] : null);
        if (excludeKeywords) {
          const excInput = this.$refs.optRoot.querySelector('[data-exc-input="1"]');
          if (excInput) {
            const val = excInput.value.trim().replace(/,+$/, '');
            if (val && !excludeKeywords.includes(val)) {
              excludeKeywords.push(val);
            }
          }
        }

        return { keywords, excludeKeywords };
      },

      async saveAndRegenerate() {
        const { keywords: domKeywords, excludeKeywords: domExcludeKeywords } = this.readOptimizeKeywordsFromDOM();

        await this.saveOptimized();

        const payload = {};
        if (domKeywords && domKeywords.length) {
          payload.keywords = domKeywords.join('\n');
        }
        if (domExcludeKeywords && domExcludeKeywords.length) {
          payload.exclude_keywords = domExcludeKeywords.join('\n');
        }

        this.updatingReports.push(this.selectedReport.id);
        await axios.post('/pdp-analysis/regenerate-report/' + this.selectedReport.id, payload);
        this.$bvToast.toast('Report generation started, please check back later!', {
          title: 'Info',
          variant: 'info',
        });
      },
      selectImageReportType(value) {
        this.selectedImageReportType = value;
        this.$emit('change-image-report-type', value);
      },
    },
  }
</script>

<style scoped lang="scss">
  .pdp-analysis-wrap {
    .pdp-tab-section {
      position: relative;
    }
    .shared-locked {
      filter: blur(8px);
      pointer-events: none;
      user-select: none;
    }
    .shared-locked-placeholder {
      min-height: 360px;
      background:
        linear-gradient(135deg, #f1f5f9 25%, transparent 25%) -20px 0,
        linear-gradient(225deg, #f1f5f9 25%, transparent 25%) -20px 0,
        linear-gradient(315deg, #f1f5f9 25%, transparent 25%),
        linear-gradient(45deg, #f1f5f9 25%, #e2e8f0 25%);
      background-size: 40px 40px;
      border-radius: 12px;
    }
    .shared-lock-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding-top: 80px;
      background: rgba(255, 255, 255, 0.4);
      z-index: 10;
    }
    .shared-lock-card {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
      padding: 28px 32px;
      text-align: center;
      max-width: 420px;
    }
    .shared-lock-icon {
      font-size: 28px;
      color: #209c86;
      margin-bottom: 8px;
      display: block;
    }
    .shared-lock-text {
      color: #334155;
      font-size: 0.95rem;
    }
    .text-foreground {
      color: #020817;
    }
    .text-muted-foreground {
      color: #64748b;
    }
    .bg-muted {
      background-color: #f1f5f9;
    }
    .mih-100 {
      min-height: 100px!important;
    }
    .last-analyzed {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin-top: 1.5rem;
      font-size: 13px;
      color: #94a3b8;

      .bi-clock-history {
        display: inline-flex;
        align-items: center;
        font-size: 14px;
        line-height: 1;
      }
    }
    .prompts-list {
      display: flex;
      flex-wrap: wrap;
      gap: 24px;
      justify-content: flex-start;
      &.items-5 {
        background: #f1f5f9;
        border-radius: 8px;
        padding: 4px;
        gap: 0;
        .prompt-item {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          padding: 0;
          font-weight: 600;
          color: #64748b;
          cursor: pointer;
          background-color: transparent;
          border: none;
          box-shadow: none;
          font-size: 14px;
          line-height: normal;
          height: 40px;
          
          &__title__text {
            display: grid;
            place-items: center;
            line-height: 1;
          }
          
          &.active {
            background: #fff;
            border: 1px solid #e7e9ee;
            color: #020817;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }
        }
      }
      
      &:not(.items-5) {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        .prompt-item {
          flex-direction: column;
          &__title {
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            
            &__text {
              color: #020817;
              font-size: 1.125rem;
              font-weight: bold;
            }
          }
          .description {
            color: #64748b;
            font-size: 14px;
            line-height: 1.5;
          }
          &.active, &:hover {
            border-color: rgba(32, 156, 134, .5);
            box-shadow: 0 4px 14px rgba(0, 0, 0, .06);
            color: #020817;
            
            .icon-wrap {
              background: #1abc9c;
              
              i {
                color: #fff;
              }
            }
          }
          .icon-wrap {
            width: 36px;
            height: 36px;
            -webkit-border-radius: 8px;
            -moz-border-radius: 8px;
            border-radius: 8px;
            background-color: rgb(26,188,156,.10);
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 8px;
            i {
              color: #1abc9c;
              margin: 0!important;
            }
          }
        }
      }

      .prompt-item {
        cursor: pointer;
        flex: 1 1 calc(33.333% - 24px);
        min-width: 266px;
        background: #fff;
        border: 1px solid #e6e8eb;
        border-radius: 12px;
        padding: 24px;
        box-shadow: 0 2px 6px rgba(0,0,0,0.03);
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        
        .icon-wrap {
          width: 20px;
          height: 20px;
          display: flex;
          justify-content: center;
          align-items: center;
          margin-right: 8px;
        }
        
        i {
          line-height: 0;
        }
        
        &__title {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        
        p {
          margin-bottom: 0;
        }
      }
    }

    .source-grid {
      --gap: 24px;
      --card-radius: 14px;
      --border: #e6e9ef;
      --ink: #12161b;
      --muted: #667085;
      --icon-bg: #e9f7f2;
      --icon-fg: #14b8a6;
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: var(--gap);
    }

    .source-card {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      border: 1px solid var(--border);
      border-radius: var(--card-radius);
      background: #fff;
      text-decoration: none;
      color: inherit;
      transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
      box-shadow: 0 2px 6px rgba(16,24,40,0.03);
    }

    .source-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 18px rgba(16,24,40,0.08);
      border-color: #d9dde6;
    }

    .source-card--active {
      border-color: #14b8a6;
      box-shadow: 0 0 0 1px #14b8a6;
      background: #f0fdf9;
    }

    .source-card--active:hover {
      border-color: #14b8a6;
      box-shadow: 0 0 0 1px #14b8a6, 0 6px 18px rgba(20,184,166,0.12);
    }

    .source-card__icon {
      flex: 0 0 auto;
      width: 56px;
      height: 56px;
      border-radius: 12px;
      background: var(--icon-bg);
      display: grid;
      place-items: center;
      color: var(--icon-fg);
      
      i {
        font-size: 25px; 
        width: 30px;
        height: 30px;
        text-align: center;
        color: #1abc9c;
      }
    }

    .source-card__body {
      min-width: 0;
      flex: 1 1 auto;
      overflow: hidden;
    }
    .source-card__title {
      margin: 2px 0 6px;
      font-size: 18px;
      line-height: 1.25;
      font-weight: 700;
      color: var(--ink);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .source-card__desc {
      margin: 0;
      color: var(--muted);
      font-size: 15px;
      line-height: 1.5;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .source-sections {
      width: 100%;
      padding: 8px 0 0;
    }
    .urls-section {

      textarea {
        padding: 8px 12px;
        width: 100%;
        min-height: 100px;
        border: 1px solid #e2e8f0;
        -webkit-border-radius: 8px;
        -moz-border-radius: 8px;
        border-radius: 8px;
        display: inline-block;
        resize: vertical;
      }
      
      .button-list {
        .copy-btn {
          border: 1px solid #e2e8f0;
          font-size: 14px;
          
          &:hover {
            background: #f1f5f9;
          }
        }
      }

      .product-list-section {
        padding: 0;
        background: transparent;
        border: none;
        border-radius: 0;

        .row {
          display: flex;

          .col-md-6 {
            flex: 1;
            min-width: 0;
          }
        }

        .multiselect-option {
          small {
            font-size: 12px;
          }
        }

        .product-select-all {
          width: auto;
          padding: 4px 24px 4px 8px;
          font-size: 13px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          background-color: #fff;
          cursor: pointer;

          &:focus {
            outline: none;
            border-color: #1abc9c;
          }
        }
      }

      .divider-with-text {
        display: flex;
        align-items: center;
        text-align: center;
        color: #64748b;
        font-size: 13px;
        font-weight: 500;

        &::before,
        &::after {
          content: '';
          flex: 1;
          border-bottom: 1px solid #e2e8f0;
        }

        span {
          padding: 0 16px;
          background: transparent;
        }
      }
    }
    
    .change-source-btn {
      color: #0f172a;
      -webkit-border-radius: 6px;
      -moz-border-radius: 6px;
      border-radius: 6px;
      
      &:hover {
        background: #f1f5f9;
      }
    }
    
    .drop-zone{
      .vue-dropzone {
        -webkit-border-radius: 6px;
        -moz-border-radius: 6px;
        border-radius: 6px;
        border-style: dotted;
      }
      ul {
        list-style: none;
        padding: 0;
      }
      .vue-dropzone .dz-upload-icon,
      .dz-upload-icon {
        color: #334155;
        margin-bottom: 10px;
        line-height: 1;
      }
      .vue-dropzone .dz-upload-icon i,
      .dz-upload-icon i {
        font-size: 56px !important;
        font-weight: 700;
        -webkit-text-stroke: 1.5px currentColor;
        display: inline-block;
        line-height: 1;
      }
      .dz-upload-title {
        font-size: 15px;
        font-weight: 600;
        color: #0f172a;
        margin-bottom: 4px;
      }
      .dz-upload-sub {
        font-size: 13px;
        color: #64748b;
        i.bi-info-circle {
          margin-left: 4px;
          cursor: help;
        }
      }
      ::v-deep .vue-dropzone.dz-started .dz-message { display: block !important; }
      ::v-deep .dz-title-has { display: none; }
      ::v-deep .vue-dropzone.has-files .dz-title-empty { display: none; }
      ::v-deep .vue-dropzone.has-files .dz-title-has { display: inline; }
      ::v-deep .vue-dropzone .dz-preview { display: none !important; }
    }

    .uploaded-files-list {
      .uploaded-file-sheet {
        display: flex;
        align-items: center;
        gap: 8px;

        &__label {
          margin: 0;
          font-size: 13px;
          color: #64748b;
          white-space: nowrap;
        }

        &__select {
          max-width: 260px;
        }
      }

      .uploaded-file-filter {
        display: flex;
        align-items: center;
        gap: 10px;

        &__label {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin: 0;
          font-size: 13px;
          color: #0f172a;
          white-space: nowrap;
        }

        &__hint {
          font-size: 12px;
          color: #94a3b8;
        }
      }
      .uploaded-file-item {
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        padding: 10px 14px;
        margin-bottom: 8px;
        background: #fff;
      }
      .uploaded-file-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 6px;
      }
      .uploaded-file-info {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        color: #0f172a;
      }
      .uploaded-file-icon {
        color: #14b8a6;
        font-size: 16px;
      }
      .uploaded-file-size {
        color: #64748b;
        font-size: 12px;
        font-weight: 400;
      }
      .btn-close-file {
        background: transparent;
        border: none;
        color: #64748b;
        cursor: pointer;
        padding: 2px 6px;
        &:hover { color: #0f172a; }
      }
      .uploaded-file-save {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .save-as-list-label {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin: 0;
        font-size: 13px;
        color: #0f172a;
        white-space: nowrap;
      }
      .save-as-list-input {
        flex: 1;
      }
    }
    
    .continue-btn {
      background: #1abc9c;
      color: #fff;
      -webkit-border-radius: 6px;
      -moz-border-radius: 6px;
      border-radius: 6px;
      padding: 10px 20px;
      font-size: 16px;
      font-weight: 600;
      line-height: 1.5;
      text-align: center;
      display: flex;
      justify-content: center;
      align-items: center;
      
      &:hover {
        background: #007964;
      }
    }
    .salsify-integration {
      h2 {
        margin: 0 0 12px;
        font-size: 14px;
        font-weight: 700;
        letter-spacing: .2px;
        display: flex;
        align-items: center;
        gap: 10px;
        color: #0f172a;
      }

      .section {
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        padding: 18px;
        box-shadow: var(--shadow);

        & + .section {
          margin-top: 18px;
        }

        .lead {
          font-size: 12px;
          color: var(--muted);
          margin: -6px 0 12px;
        }
      }

      .status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #1abc9c;
      }

      .input-row {
        display: flex;
        gap: 10px;
        align-items: stretch;
        background: #f1f5f9;
        border: 1px solid var(--border);
        border-radius: 10px;
        padding: 10px;

        input[type="text"] {
          flex: 1;
          border: 0;
          background: transparent;
          font-size: 13px;
          outline: none;
          color: #0b1220;
          min-width: 0;
        }
      }

      .btn {
        &.secondary {
          --btn-bg: #0b1325;
          --btn-bg-hover: #111a2c;
          --btn-color: #fff;
        }

        &.ghost {
          --btn-bg: #fff;
          --btn-bg-hover: #f8fafc;
          --btn-color: #0b1220;
          border-color: var(--border);
        }
      }

      .help {
        font-size: 12.5px;
        margin-top: 5px;
      }

      .tabs {
        background: #f1f5f9;
        border-radius: 8px;
        padding: 4px;
        gap: 0;
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start;
      }

      .tab {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        padding: 15px 0;
        font-weight: 600;
        color: #64748b;
        cursor: pointer;
        background-color: transparent;
        border: none;
        box-shadow: none;
        font-size: 14px;
        line-height: normal;

        &.active {
          background: #fff;
          border: 1px solid #e7e9ee;
          color: #020817;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
      }

      .tabpanes {
        margin-top: 14px;
      }

      label {
        display: block;
        font-weight: 700;
        margin: 0 0 8px;
      }

      textarea,
      select {
        width: 100%;
        padding: 12px 14px;
        border-radius: 12px;
        border: 1px solid var(--border);
        background: #fff;
        font: inherit;
      }

      textarea {
        padding: 8px 12px;
        width: 100%;
        height: 100%;
        min-height: 160px;
        border: 1px solid #e2e8f0;
        -webkit-border-radius: 8px;
        -moz-border-radius: 8px;
        border-radius: 8px;
        display: inline-block;
      }

      select {
        appearance: none;
        background-image:
          linear-gradient(45deg, transparent 50%, #64748b 50%),
          linear-gradient(135deg, #64748b 50%, transparent 50%),
          linear-gradient(to right, transparent, transparent);
        background-position:
          calc(100% - 20px) calc(1em + 2px),
          calc(100% - 15px) calc(1em + 2px),
          100% 0;
        background-size: 5px 5px, 5px 5px, 2.5em 2.5em;
        background-repeat: no-repeat;
      }

      .form-row {
        margin-top: 12px;
        
        label {
          font-size: 14px;
          color: #0f172a;
        }
      }

      .actions {
        margin-top: 18px;
        display: flex;
        gap: 12px;
        justify-content: flex-end;
        flex-wrap: wrap;
      }

      .mini-note {
        margin-top: 8px;
        color: var(--muted-2);
        font-size: 12.5px;

        a {
          color: #0ea5e9;
          text-decoration: none;

          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
    .optimize-report {
      background: #fff;
      border: 1px solid var(--bs-border-color, #e5e7eb);
      border-radius: .75rem;
      padding: 16px;

      .analysis-summary-accordion {
        border: 1px solid var(--bs-border-color, #e5e7eb);
        border-radius: .75rem;
        background: #fff;
        overflow: hidden;

        .accordion-header {
          padding: 16px 20px;
          cursor: pointer;
          background: #f8f9fa;
          transition: background-color 0.2s ease;

          &:hover {
            background: #e9ecef;
          }

          .accordion-header-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;

            .accordion-icon {
              font-size: 16px;
              color: #64748b;
              transition: transform 0.2s ease;
              margin-left: auto;
            }

            .accordion-title {
              margin: 0;
              font-size: 18px;
              font-weight: 700;
              color: #020817;
            }
          }
        }

        .accordion-body {
          padding: 20px;
          border-top: 1px solid var(--bs-border-color, #e5e7eb);

          .score-wrap {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
            margin-bottom: 0;
          }

          .score-card {
            background: #fff;
            border: 1px solid var(--bs-border-color, #e5e7eb);
            border-radius: .75rem;
            padding: 16px;
            display: flex;
            flex-direction: column;
            gap: 10px;
          }

          .score {
            font-size: 28px;
            font-weight: 700;
            .current { color: #111827 }
          }

          .issues-count {
            font-size: 28px;
            font-weight: 700;
            margin: 0;
          }

          .badges-list {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            .badge { border-radius: 999px; padding: 6px 10px; font-weight: 600 }
          }

          .gains-card {
            display: flex;
            flex-direction: column;
            .gains-item {
              font-size: 28px;
              font-weight: 700;
              color: #16a34a;
              line-height: 1;
              margin-bottom: 4px;
            }
          }

          .badge-danger { background: #fee2e2; color: #b91c1c }
          .badge-warning { background: #fef3c7; color: #92400e }
          .badge-info { background: #dbeafe; color: #1e40af }

          .progress {
            height: 8px;
            background-color: #e5e7eb;
            border-radius: 999px;
            overflow: hidden;

            .progress-bar {
              height: 100%;
              transition: width 0.3s ease;
            }
          }

          @media (max-width: 991.98px) {
            .score-wrap { grid-template-columns: 1fr }
          }
        }
      }
    }

    .kw-rankings {
      .kw-card {
        background: #fff;
        border: 1px solid var(--bs-border-color, #e5e7eb);
        border-radius: .75rem;
        padding: 20px;
        
        .button-list {
          .btn {
            border: 1px solid #e2e8f0;
            font-size: 14px;

            &:hover {
              background: #f1f5f9;
            }
          }
        }
      }

      .kw-head {
        margin-bottom: 12px;
      }

      .kw-kpis {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 16px;
        padding: 6px 0 18px;
      }

      .kpi {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .kpi-label {
        font-size: .95rem;
      }

      .kpi-value {
        font-weight: 800;
        font-size: 42px;
        line-height: 1;
      }

      .kw-table {
        border-collapse: separate;
        border-spacing: 0;
        thead th {
          font-weight: 600;
          color: #6b7280;
          border-bottom: 1px solid var(--bs-border-color, #e5e7eb);
          background: #fff;
        }
        tbody td {
          vertical-align: middle;
          padding-top: 16px;
          padding-bottom: 16px;
          border-top: 1px solid var(--bs-border-color, #eef0f3);
        }
        .kw-text {
          color: #111827;
        }
        .delete-keyword {
          cursor: pointer;
        }
      }

      .change-pill {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 6px 12px;
        border-radius: 999px;
        font-weight: 700;
        background: #d1fae5;
        color: #065f46;
        line-height: 1;
      }

      @media (max-width: 991.98px) {
        .kw-kpis { grid-template-columns: 1fr }
        .kpi-value { font-size: 34px }
      }
    }
    .add-form {
      width: 262px;

      .keyword-input {
        flex: 1;
        font-size: 0.9rem;
        padding: 6px 10px;
      }

      .add-btn {
        background: transparent;
        border: 1px solid #e2e8f0;
        color: var(--bs-primary);
        font-size: 0.85rem;
        padding: 6px 10px;
        border-radius: 6px;
        transition: all 0.2s ease;

        &:hover {
          background: #f1f5f9;
        }
      }
    }
    ::v-deep .recommendation-table {
      width: 100%;
      table-layout: fixed;

      th, td {
        vertical-align: top;
        word-break: break-word;
      }

      th:nth-child(1),
      td:nth-child(1) {
        width: 25%;
      }

      th:nth-child(2),
      td:nth-child(2) {
        width: 35%;
      }

      th:nth-child(3),
      td:nth-child(3) {
        width: 40%;
      }
    }

    ::v-deep .recommendations-panel {
      .recommendations-body {
        padding: 0;
      }

      .recommendation-section {
        padding: 20px 24px;
        border-bottom: 1px solid var(--bs-border-color, #e5e7eb);

        &:last-child {
          border-bottom: none;
        }
      }

      .recommendation-section-title {
        font-size: 15px;
        font-weight: 700;
        color: #111827;
        margin: 0 0 16px 0;
      }

      .recommendation-table-wrap {
        background: #fff;
        border: 1px solid var(--bs-border-color, #e5e7eb);
        border-radius: 8px;
        overflow: hidden;
      }

      .recommendation-table {
        width: 100%;
        border-collapse: collapse;
        margin: 0;

        thead tr {
          background: #f8fafc;
        }

        th {
          padding: 12px 16px;
          font-size: 13px;
          font-weight: 600;
          color: #64748b;
          text-align: left;
          border-bottom: 1px solid var(--bs-border-color, #e5e7eb);
        }

        td {
          padding: 14px 16px;
          font-size: 14px;
          line-height: 1.5;
          color: #334155;
          border-bottom: 1px solid var(--bs-border-color, #e5e7eb);
          vertical-align: top;
        }

        tbody tr:last-child td {
          border-bottom: none;
        }

        .col-issue {
          width: 15%;
          font-weight: 600;
          color: #111827;
        }

        .col-actual {
          width: 30%;
          color: #64748b;
        }

        .col-recommendation {
          width: 55%;
          color: #334155;
        }
      }

      .recommended-keywords {
        margin-top: 20px;

        .keywords-label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          color: #374151;
          margin-bottom: 10px;
        }

        .keywords-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .keyword-pill {
          display: inline-block;
          padding: 6px 14px;
          background: #f1f5f9;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          color: #334155;
        }
      }
    }
  }
  .img-thumb {
    border-radius: 4px;
    overflow: hidden;
    display: inline-flex;
    box-shadow:
      0 0 0 1px rgba(0,0,0,.10),
      0 0 0 3px rgba(255,255,255,.70);
    cursor: pointer;
    min-width: 40px;
    width: 40px;
  }

  .img-thumb__img {
    display: block;
    width: 40px;
    height: 40px;
    object-fit: cover;
  }

  .img-thumb:hover {
    box-shadow:
      0 0 0 1px rgba(0,0,0,.18),
      0 0 0 3px rgba(255,255,255,.80),
      0 6px 16px rgba(0,0,0,.12);
  }

  ::v-deep .opt-editable-box {
    position: relative;
    transition: all 0.2s ease;

    &:hover {
      border-color: #99f6e4 !important;
      box-shadow: 0 4px 12px rgba(20, 184, 166, 0.05);

      .opt-pencil {
        opacity: 1;
      }
    }

    /* Content styling (lists/paragraphs) */
    ul, ol {
      margin-bottom: 0;
      padding-left: 20px;

      li {
        color: #111827;
        margin-bottom: 12px;

        &:last-child {
          margin-bottom: 0;
        }

        &::marker {
          color: #99f6e4; /* Light teal bullets */
        }
      }
    }

    /* The Pencil icon */
    .opt-pencil {
      position: absolute;
      top: 16px;
      right: 8px;
      background: transparent !important;
      border: none !important;
      font-size: 1rem;
      color: #64748b;
      line-height: 1;
      opacity: 0;
      transition: opacity 0.2s ease, transform 0.2s ease, color 0.2s ease;
      cursor: pointer;
      box-shadow: none !important;

      &:hover {
        color: #14b8a6 !important;
      }
    }

    &.opt-active {
      background-color: #fff;
      border-color: #14b8a6 !important;
      box-shadow: 0 0 0 4px rgba(20, 184, 166, 0.1);
      outline: none;
    }
  }

  .pdp-edit-toolbar {
    position: sticky;
    bottom: 0;
    background: #fff;
    padding: 12px 16px;
    z-index: 100;
    border-top: 1px solid var(--bs-border-color, #e5e7eb);
    margin: 0 -16px -16px; /* Offset .optimize-report padding */
    border-radius: 0 0 .75rem .75rem;
    box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.05);
  }

  ::v-deep .opt-root {
    .border.rounded.bg-white {
      background-color: #f0fdfa !important; /* Light teal background */
      border: 1px solid #ccfbf1 !important; /* Soft teal border */

      &.opt-active {
        background-color: transparent !important;

        mark {
          background: transparent !important;
        }

        .opt-pencil {
          display: none;
        }
      }
    }

    .small {
      font-weight: 600;
      margin-bottom: 12px !important;
      display: block;
    }

    .opt-before-kw-box,
    .opt-before-exc-box {
      position: relative;

      .opt-pencil {
        position: absolute;
        top: 16px;
        right: 8px;
        background: transparent !important;
        border: none !important;
        font-size: 1rem;
        color: #64748b;
        line-height: 1;
        opacity: 0;
        transition: opacity 0.2s ease, color 0.2s ease;
        cursor: pointer;
        box-shadow: none !important;

        &:hover {
          color: #14b8a6 !important;
        }
      }

      &:hover .opt-pencil {
        opacity: 1;
      }
    }

    .kw-edit-wrap {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px;
    }

    .kw-edit-pills {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .kw-edit-pill {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 10px;
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 500;
      color: #334155;

      .kw-pill-remove {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        padding: 0;
        margin-left: 2px;
        width: 16px;
        height: 16px;
        font-size: 14px;
        line-height: 1;
        color: #94a3b8;
        cursor: pointer;

        &:hover {
          color: #ef4444;
        }
      }
    }

    .kw-edit-input {
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 4px 10px;
      font-size: 13px;
      color: #334155;
      outline: none;
      min-width: 180px;

      &:focus {
        border-color: #14b8a6;
        box-shadow: 0 0 0 2px rgba(20, 184, 166, 0.1);
      }
    }

    .kw-before-pills-wrap,
    .exc-before-pills-wrap {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .kw-before-pill,
    .exc-before-pill {
      display: inline-block;
      padding: 4px 10px;
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 500;
      color: #334155;
    }
  }

  /* Lightbox */
  .lightbox {
    position: fixed;
    inset: 0;
    z-index: 2000;
    background: rgba(0,0,0,.60);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
  }

  .lightbox__img {
    max-width: min(980px, 95vw);
    max-height: 85vh;
    border-radius: 14px;
    box-shadow: 0 20px 60px rgba(0,0,0,.35);
    background: #fff;
  }

  .lightbox__close {
    position: fixed;
    top: 14px;
    right: 14px;
    width: 40px;
    height: 40px;
    border: 0;
    border-radius: 10px;
    background: rgba(255,255,255,.9);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  %score-badge-base {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    line-height: 1;
    white-space: nowrap;
  }

  .report-multiselect {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
  }

  .report-dropdown-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 8px 12px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    color: #020817;
    cursor: pointer;
    text-align: left;
    transition: border-color 0.15s;

    &:hover {
      border-color: #cbd5e1;
    }

    &:focus {
      outline: none;
      border-color: #14b8a6;
      box-shadow: 0 0 0 2px rgba(20, 184, 166, 0.1);
    }

    &__text {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
      overflow: hidden;
      width: 100%;
    }

    &__id {
      font-weight: 600;
      color: #020817;
      flex-shrink: 0;
    }

    &__sep {
      color: #94a3b8;
      flex-shrink: 0;
    }

    &__name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #020817;
    }

    &__placeholder {
      color: #94a3b8;
    }
  }

  .report-dropdown-menu {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 50;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    overflow: hidden;
  }

  .report-dropdown-search {
    position: relative;
    padding: 8px;
    border-bottom: 1px solid #f1f5f9;

    &__icon {
      position: absolute;
      left: 18px;
      top: 50%;
      transform: translateY(-50%);
      color: #94a3b8;
      font-size: 14px;
    }

    &__input {
      width: 100%;
      padding: 8px 8px 8px 32px;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      font-size: 14px;
      color: #020817;
      outline: none;

      &::placeholder {
        color: #94a3b8;
      }

      &:focus {
        border-color: #14b8a6;
        box-shadow: 0 0 0 2px rgba(20, 184, 166, 0.1);
      }
    }
  }

  .report-dropdown-list {
    list-style: none;
    margin: 0;
    padding: 4px 0;
    max-height: 240px;
    overflow-y: auto;
  }

  .report-dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    cursor: pointer;
    transition: background-color 0.1s;

    &:hover {
      background-color: #f8fafc;
    }

    &.is-selected {
      background-color: #eff6ff;
    }

    &__check {
      color: #3b82f6;
      font-size: 16px;
      flex-shrink: 0;
    }

    &__id {
      font-size: 13px;
      font-weight: 600;
      color: #94a3b8;
      flex-shrink: 0;
    }

    &__name {
      font-size: 14px;
      color: #020817;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__score {
      @extend %score-badge-base;
      margin-left: auto;
      padding: 4px 8px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 700;
      flex-shrink: 0;
    }

    &--empty {
      color: #94a3b8;
      justify-content: center;
      cursor: default;

      &:hover {
        background-color: transparent;
      }
    }
  }

  .report-score-badge {
    @extend %score-badge-base;

    z-index: 2;
    padding: 6px 10px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 700;
    pointer-events: none;
    margin: 0 12px 0 auto;
  }

  .report-history-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    color: #334155;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 6px 12px;
    background: #fff;

    .bi-clock-history {
      display: inline-flex;
      align-items: center;
      line-height: 1;
    }

    &:hover {
      background: #f8fafc;
      border-color: #cbd5e1;
    }

    &__dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #209c86;
      flex-shrink: 0;
    }

    &__count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 22px;
      height: 22px;
      padding: 0 6px;
      border-radius: 11px;
      background: #f1f5f9;
      font-size: 12px;
      font-weight: 600;
      color: #334155;
      line-height: 1;
    }
  }

  /* ── Report History ── */
  .report-history-wrap {
    position: relative;
  }

  .report-history-panel {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 100;
    width: 480px;
    overflow: visible;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    padding: 20px;

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 16px;
    }

    &__title {
      font-size: 18px;
      font-weight: 700;
      color: #020817;
      margin: 0;
    }

    &__scope-toggle {
      display: inline-flex;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      overflow: hidden;
    }

    &__scope-btn {
      padding: 6px 14px;
      font-size: 13px;
      font-weight: 500;
      border: none;
      background: #fff;
      color: #64748b;
      cursor: pointer;
      transition: background 0.15s, color 0.15s;

      &.is-active {
        background: #f1f5f9;
        color: #020817;
        font-weight: 600;
      }

      &:hover:not(.is-active) {
        background: #f8fafc;
      }
    }

    &__filters {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-bottom: 16px;
    }

  }

  .report-history-filter {
    display: flex;
    align-items: center;
    gap: 10px;

    &__icon {
      color: #94a3b8;
      font-size: 16px;
      flex-shrink: 0;
    }

    &__select-wrap {
      position: relative;
      flex: 1 1 auto;
      min-width: 0;
    }

    &__select {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 8px 12px;
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      font-size: 14px;
      color: #020817;
      cursor: pointer;

      &:hover {
        border-color: #cbd5e1;
      }
    }

    &__dropdown {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      right: 0;
      z-index: 50;
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
      list-style: none;
      margin: 0;
      padding: 4px 0;
      max-height: 220px;
      overflow-y: auto;
    }

    &__dropdown-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 12px;
      font-size: 14px;
      color: #020817;
      cursor: pointer;

      &:hover {
        background: #f8fafc;
      }

      &.is-selected {
        background: #eff6ff;
        font-weight: 500;
      }

      .bi-check2 {
        color: #3b82f6;
      }
    }

    &__pick-wrap {
      position: relative;
      flex-shrink: 0;
    }

    &__pick-btn {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 13px;
      font-weight: 500;
      white-space: nowrap;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 6px 10px;

      .bi-calendar3 {
        display: inline-flex;
        align-items: center;
        line-height: 1;
      }
    }

    &__clear-btn {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      font-size: 13px;
      white-space: nowrap;
      text-decoration: none;

      .bi-x {
        display: inline-flex;
        align-items: center;
        line-height: 1;
      }
    }
  }

  /* Date picker */
  .report-history-datepicker {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    z-index: 60;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    padding: 12px;
    width: 280px;

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 8px;
    }

    &__month-label {
      font-size: 14px;
      font-weight: 600;
      color: #020817;
    }

    &__nav {
      border: none;
      background: none;
      padding: 4px 8px;
      cursor: pointer;
      color: #64748b;
      border-radius: 6px;

      &:hover {
        background: #f1f5f9;
      }
    }

    &__weekdays {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      text-align: center;
      font-size: 12px;
      font-weight: 600;
      color: #64748b;
      margin-bottom: 4px;
    }

    &__days {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 2px;
    }

    &__day {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border: none;
      background: none;
      border-radius: 8px;
      font-size: 13px;
      color: #020817;
      cursor: pointer;

      &:hover {
        background: #f1f5f9;
      }

      &.is-other-month {
        color: #cbd5e1;
      }

      &.is-today {
        border: 1px solid #e2e8f0;
      }

      &.is-selected {
        background: #f1f5f9;
        font-weight: 600;
      }

      &:not(.is-disabled):not(.is-other-month) {
        background: #e6f7f3;
        color: #0f5132;
        font-weight: 600;

        &:hover {
          background: #ccf0e6;
        }
      }

      &.is-disabled {
        color: #e2e8f0;
        cursor: default;
        background: none;
        font-weight: 400;

        &:hover {
          background: none;
        }
      }
    }
  }

  /* History list */
  .report-history-list {
    max-height: 320px;
    overflow-y: auto;
    overflow-x: hidden;
    margin: 0 -20px;
    padding: 0 20px;

    &__item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 20px;
      margin: 0 -20px;
      border-top: 1px solid #f1f5f9;
      cursor: pointer;
      transition: background 0.1s;

      &:hover {
        background: #f8fafc;
      }

      &.is-active {
        background: #eff6ff;
      }
    }

    &__icon {
      font-size: 20px;
      color: #94a3b8;
      flex-shrink: 0;
    }

    &__info {
      flex: 1 1 auto;
      min-width: 0;
    }

    &__name {
      font-size: 14px;
      font-weight: 500;
      color: #020817;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__meta {
      font-size: 13px;
      color: #94a3b8;
      margin-top: 2px;
    }

    &__score {
      flex-shrink: 0;
      font-size: 13px;
      font-weight: 700;
      color: #fff;
      border-radius: 14px;
      padding: 4px 12px;
      min-width: 36px;
      text-align: center;
    }

    &__empty {
      padding: 24px 0;
      text-align: center;
      color: #94a3b8;
      font-size: 14px;
    }
  }

  .back-to-reports-btn {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    background: none;
    border: none;
    color: #6b7280;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    padding: 0.375rem 0;
    transition: color 0.15s ease;
  }
  .back-to-reports-btn:hover {
    color: #374151;
  }
  
  .iframe-tab {
    min-width: 245px!important;
  }

  .reports-type-dropdown__toggle {
    width: 44px;
    height: 44px;
    border-radius: 12px !important;
    border: 1px solid #d9dee7 !important;
    background: #fff !important;
    color: #111827 !important;
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    box-shadow: none !important;
    padding: 0 !important;
  }

  .reports-type-dropdown__toggle:hover,
  .reports-type-dropdown__toggle:focus,
  .reports-type-dropdown__toggle:active {
    background: #fff !important;
    border-color: #cfd6e1 !important;
    box-shadow: none !important;
  }

  ::v-deep .reports-type-dropdown__menu {
    width: 480px;
    padding: 0 !important;
    border-radius: 20px !important;
    border: 1px solid #dfe3eb !important;
    overflow: hidden;
    box-shadow: 0 14px 40px rgba(15, 23, 42, 0.12) !important;
    max-height: 500px;
  }

  .reports-type-dropdown__header {
    padding: 15px 14px 10px;
    border-bottom: 1px solid #e5e7eb;
    background: #fff;
  }

  .reports-type-dropdown__title {
    font-size: 15px;
    line-height: 1.2;
    font-weight: 700;
    color: #111827;
    margin-bottom: 8px;
  }

  .reports-type-dropdown__subtitle {
    font-size: 13px;
    line-height: 1.1;
    color: #667085;
  }

  .reports-type-dropdown__list {
    padding: 6px 0;
    background: #fff;
    max-height: 300px;
    overflow: scroll;
  }

  .reports-type-dropdown__option {
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: 5px;
    text-align: left;
    padding: 5px 10px;
    border: 0;
    background: transparent;
    cursor: pointer;
  }

  .reports-type-dropdown__option:hover {
    background: #f8fafc;
  }

  .reports-type-dropdown__radio {
    width: 15px;
    height: 15px;
    min-width: 15px;
    border-radius: 50%;
    border: 2px solid #58c3a5;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
    background: #fff;
  }

  .reports-type-dropdown__radio-inner {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #58c3a5;
  }

  .reports-type-dropdown__content {
    display: flex;
    flex-direction: column;
  }

  .reports-type-dropdown__label {
    font-size: 14px;
    line-height: 1.1;
    font-weight: 600;
    color: #111827;
    margin-bottom: 4px;
  }

  .reports-type-dropdown__description {
    font-size: 13px;
    line-height: 1.1;
    color: #667085;
  }

  .reports-type-dropdown__option.is-active .reports-type-dropdown__label {
    color: #111827;
  }
  .reports-type-dropdown__tabs {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
    padding: 12px 16px 10px;
    border-bottom: 1px solid #e5e7eb;
  }

  .reports-type-dropdown__tab {
    border: 1px solid #dbe3ee;
    background: #f8fafc;
    color: #64748b;
    border-radius: 10px;
    padding: 8px 10px;
    font-size: 13px;
    line-height: 1.2;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .reports-type-dropdown__tab:hover {
    background: #f1f5f9;
    color: #334155;
  }

  .reports-type-dropdown__tab.is-active {
    background: #209c86;
    border-color: #1c8c78;
    color: #ffffff;
  }

  .reports-type-dropdown__current {
    margin: 12px 16px 4px;
    padding: 10px 12px;
    border: 1px solid #dbeafe;
    background: #eff6ff;
    border-radius: 12px;
  }

  .reports-type-dropdown__current-label {
    margin-bottom: 2px;
    font-size: 10px;
    line-height: 1.2;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #64748b;
  }

  .reports-type-dropdown__current-value {
    font-size: 13px;
    line-height: 1.35;
    font-weight: 800;
    color: #0f172a;
  }
  .reports-type-dropdown__search {
    position: relative;
    margin: 12px 16px 8px;
  }

  .reports-type-dropdown__search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 14px;
    pointer-events: none;
  }

  .reports-type-dropdown__search-input {
    width: 100%;
    height: 40px;
    padding: 8px 38px 8px 36px;
    border: 1px solid #dbe3ee;
    border-radius: 12px;
    background: #ffffff;
    color: #0f172a;
    font-size: 13px;
    font-weight: 600;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .reports-type-dropdown__search-input::placeholder {
    color: #94a3b8;
    font-weight: 500;
  }

  .reports-type-dropdown__search-input:focus {
    border-color: #5ec4ae;
    box-shadow: 0 0 0 3px rgba(94, 196, 174, 0.18);
  }

  .reports-type-dropdown__search-clear {
    position: absolute;
    right: 8px;
    top: 50%;
    width: 26px;
    height: 26px;
    transform: translateY(-50%);
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #64748b;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .reports-type-dropdown__search-clear:hover {
    background: #f1f5f9;
    color: #0f172a;
  }

  .reports-type-dropdown__empty {
    padding: 18px 16px 20px;
    color: #64748b;
    font-size: 13px;
    font-weight: 600;
    text-align: center;
  }
</style>
